import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Pause, 
  Play, 
  Square, 
  Lock,
  Unlock,
  Volume2,
  MapPin,
  MessageCircle,
  Trash2,
  Pencil,
  Navigation,
  Frown,
  Meh,
  Smile,
  Zap,
  Flame,
  Footprints,
  Trophy,
  Maximize2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SAFE_AREA } from "@/utils/safeArea";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { Geolocation, Position } from "@capacitor/geolocation";
import { prepareBackgroundKeepAlive } from "@/lib/straivNowPlaying";
import { toast } from "sonner";
import { Analytics } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { PlatformMap } from "@/components/PlatformMap";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useWorkouts } from "@/hooks/useWorkouts";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import type { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { RunLiveActivity } from "@/lib/runLiveActivity";
import type { GpsActivityKind } from "@/lib/gpsCardioWorkout";
import { exerciseNameForGpsActivity, workoutTypeForGpsActivity } from "@/lib/gpsCardioWorkout";
import {
  buildLocalRunningGuidance,
  getCoachMinIntervalSeconds,
  parseRunningPlan,
  parseRunningSettingsFromStorage,
  normalizeCoachPlanText,
  requestLiveRunningGuidance,
  type RunningCoachGuideSettings,
} from "@/lib/runningCoach";
import {
  buildHrZones,
  bucketHrZones,
  buildHrZoneVoiceCue,
  classifyBpm,
  formatHeartRateSummaryForCoach,
  type HrSample,
  type HrZoneId,
} from "@/lib/heartRateZones";
import {
  readHeartRateSamplesInRange,
  readLatestHeartRateBpm,
  getHealthConnectedState,
  getHealthConnectedCacheSync,
} from "@/lib/appleHealth";
import { HeartRateZoneChart } from "@/components/HeartRateZoneChart";
import { RouteHrEffortChart } from "@/components/RouteHrEffortChart";
import { buildZoneColoredRouteSegments } from "@/lib/routeHrZones";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import {
  parseRunPlan,
  getActivePhase,
  describeActivePhaseForCoach,
  resolveTargetPaceForPhase,
  buildPlanIntroSpeech,
  type ParsedRunStructure,
} from "@/lib/runPlanParser";
import {
  comparePaceToTarget,
} from "@/lib/runningCoach";
import {
  resetCoachVoiceSession,
  speakCoachCue,
  speakNativeNow,
  stopCoachVoice,
} from "@/lib/naturalVoice";

// BackgroundGeolocation for iOS - enables GPS when phone is locked
const BG_GEO_NAMES = ["BackgroundGeolocation", "CapacitorCommunityBackgroundGeolocation"];
let _bgGeo: any = null;
const getBackgroundGeolocation = () => {
  if (_bgGeo) return _bgGeo;
  for (const name of BG_GEO_NAMES) {
    try {
      const p = registerPlugin(name) as any;
      if (p && typeof (p as any).addWatcher === "function") {
        _bgGeo = p;
        return _bgGeo;
      }
    } catch (_) {}
  }
  return null;
};

// Haversine formula to calculate distance between two GPS points (in meters)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

// Format seconds to HH:MM:SS (rounded for display)
const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format pace (min/km) - always whole seconds, no decimals (e.g. 0:37 not 0:37.5)
const formatPace = (paceSeconds: number): string => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return "--:--";
  const minutes = Math.floor(paceSeconds / 60);
  const seconds = Math.round(paceSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Map disabled temporarily due to react-leaflet bundling issues on iOS
// const RunningMap = lazy(() => import("@/components/running/RunningMap"));

interface GpsPoint {
  lat: number;
  lng: number;
  timestamp: number;
  accuracy?: number;
}

type SummaryHeartRate = {
  zones: Parameters<typeof HeartRateZoneChart>[0]['zones'];
  buckets: Parameters<typeof HeartRateZoneChart>[0]['buckets'];
  maxHr?: number;
  maxHrSource?: 'measured' | 'estimated';
  avgBpm?: number | null;
  peakBpm?: number | null;
  targetZone?: import('@/lib/heartRateZones').HrZoneId;
};

function CarouselExpandSurface({
  onExpand,
  ariaLabel,
  carouselApi,
  className,
  children,
}: {
  onExpand: () => void;
  ariaLabel: string;
  carouselApi?: CarouselApi;
  className?: string;
  children: ReactNode;
}) {
  const touchRef = useRef({ x: 0, y: 0, t: 0, moved: false });
  const dragRef = useRef(false);

  useEffect(() => {
    if (!carouselApi) return;
    const onScroll = () => { dragRef.current = true; };
    const onSettle = () => { dragRef.current = false; };
    carouselApi.on('scroll', onScroll);
    carouselApi.on('settle', onSettle);
    return () => {
      carouselApi.off('scroll', onScroll);
      carouselApi.off('settle', onSettle);
    };
  }, [carouselApi]);

  return (
    <div
      className={cn('relative', className)}
      onTouchStart={(e) => {
        dragRef.current = false;
        touchRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          t: Date.now(),
          moved: false,
        };
      }}
      onTouchMove={(e) => {
        const dx = e.touches[0].clientX - touchRef.current.x;
        const dy = e.touches[0].clientY - touchRef.current.y;
        if (Math.hypot(dx, dy) > 14) touchRef.current.moved = true;
      }}
      onTouchEnd={() => {
        if (dragRef.current || touchRef.current.moved) return;
        if (Date.now() - touchRef.current.t > 400) return;
        onExpand();
      }}
      onClick={() => {
        if (dragRef.current || touchRef.current.moved) return;
        onExpand();
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(); }
      }}
    >
      {children}
    </div>
  );
}

function SummaryMapHrCarousel({
  mapCenter,
  routeSegments,
  routePoints,
  routeLine,
  coloredRouteSegments,
  onExpandMap,
  onExpandHr,
  onExpandCombined,
  expandMapAria,
  expandHrAria,
  expandCombinedAria,
  heartRate,
}: {
  mapCenter: { lat: number; lng: number } | null;
  routeSegments: [number, number][][];
  routePoints: { lat: number; lng: number; timestamp?: number }[];
  routeLine: [number, number][];
  coloredRouteSegments: ReturnType<typeof buildZoneColoredRouteSegments>;
  onExpandMap: () => void;
  onExpandHr: () => void;
  onExpandCombined: () => void;
  expandMapAria: string;
  expandHrAria: string;
  expandCombinedAria: string;
  heartRate: SummaryHeartRate & { samples?: { timestamp: number; bpm: number }[] };
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [page, setPage] = useState(0);
  const pageCount = 3;

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setPage(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    onSelect();
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  const pageHints = ['Swipe for heart rate zones →', 'Swipe for route + HR effort →', ''];

  return (
    <div className="shrink-0 mb-2">
      <Carousel
        setApi={setCarouselApi}
        opts={{ align: 'start', loop: false, dragFree: false, duration: 28, skipSnaps: false }}
        className="w-full touch-pan-y"
      >
        <CarouselContent className="ml-0">
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandMap}
              ariaLabel={expandMapAria}
              className="rounded-lg overflow-hidden border border-border/30 h-44 min-h-[176px] cursor-pointer"
            >
              <PlatformMap
                key="summary-map-carousel"
                center={mapCenter}
                routePoints={routeLine}
                routeSegments={coloredRouteSegments.length > 0 ? undefined : routeSegments}
                coloredRouteSegments={coloredRouteSegments.length > 0 ? coloredRouteSegments : undefined}
                showUserMarker={false}
                showRoute
                showStartFinishMarkers
                showKmMarkers
                fitToRoute
                fitPaddingBottom={1.25}
                autoFollow={false}
                className="w-full h-full pointer-events-none"
                darkMode
                interactive={false}
                zoom={12}
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-5 w-5 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandHr}
              ariaLabel={expandHrAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-y-auto cursor-pointer"
            >
              <HeartRateZoneChart
                zones={heartRate.zones}
                buckets={heartRate.buckets}
                maxHr={heartRate.maxHr}
                maxHrSource={heartRate.maxHrSource}
                avgBpm={heartRate.avgBpm}
                peakBpm={heartRate.peakBpm}
                targetZone={heartRate.targetZone}
                className="text-xs"
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-4 w-4 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandCombined}
              ariaLabel={expandCombinedAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-hidden cursor-pointer"
            >
              <div className="h-24 rounded overflow-hidden mb-2 border border-border/20">
                <PlatformMap
                  key="summary-combined-map"
                  center={mapCenter}
                  routePoints={routeLine}
                  routeSegments={coloredRouteSegments.length > 0 ? undefined : routeSegments}
                  coloredRouteSegments={coloredRouteSegments.length > 0 ? coloredRouteSegments : undefined}
                  showUserMarker={false}
                  showRoute
                  showStartFinishMarkers
                  showKmMarkers
                  fitToRoute
                  fitPaddingBottom={1.25}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode
                  interactive={false}
                  zoom={12}
                />
              </div>
              <RouteHrEffortChart
                routePoints={routePoints}
                hrSamples={heartRate.samples ?? []}
                zones={heartRate.zones}
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-4 w-4 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <div className="flex justify-center gap-1.5 mt-1.5" aria-hidden>
        {Array.from({ length: pageCount }).map((_, i) => (
          <span key={i} className={cn('h-1 w-5 rounded-full transition-colors', page === i ? 'bg-primary' : 'bg-muted-foreground/30')} />
        ))}
      </div>
      {pageHints[page] ? (
        <p className="text-[10px] text-center text-muted-foreground mt-0.5">{pageHints[page]}</p>
      ) : null}
    </div>
  );
}

export const RunningActive = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { createWorkout, addExerciseToWorkout, deleteWorkout } = useWorkouts();
  
  // Run state
  const [isRunning, setIsRunning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [pauseCount, setPauseCount] = useState(0);
  
  // Dialog states
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showRunSummary, setShowRunSummary] = useState(false);
  const [showAnalyzePrompt, setShowAnalyzePrompt] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExpandedMap, setShowExpandedMap] = useState(false);
  const [showExpandedHr, setShowExpandedHr] = useState(false);
  const [showExpandedCombined, setShowExpandedCombined] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBatteryPrompt, setShowBatteryPrompt] = useState(false);
  const [isSavingRun, setIsSavingRun] = useState(false);
  const [isStoppingRun, setIsStoppingRun] = useState(false);
  const runSaveInFlightRef = useRef(false);
  const savedWorkoutIdRef = useRef<string | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [workoutToShare, setWorkoutToShare] = useState<ShareableWorkout | null>(null);
  
  // Map re-center state - use separate mapCenter to prevent constant refresh
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [isMapCentered, setIsMapCentered] = useState(true); // Track if map is centered on user
  
  // Track if stop was confirmed (to prevent "run resumed" being said)
  const stopConfirmedRef = useRef(false);
  const [workoutSummary, setWorkoutSummary] = useState<any>(null);
  const [runNotes, setRunNotes] = useState("");
  const [runTitle, setRunTitle] = useState("");
  const [feelingEmoji, setFeelingEmoji] = useState<string>("");
  const [kmSplits, setKmSplits] = useState<{ km: number; time: string; pace: string }[]>([]);
  
  // GPS signal strength
  const [gpsSignal, setGpsSignal] = useState<'searching' | 'weak' | 'medium' | 'strong'>('searching');
  
  // Track distance for splits
  const lastSplitKmRef = useRef<number>(0);
  const splitTimesRef = useRef<{ km: number; elapsedSeconds: number }[]>([]);
  const elapsedSecondsRef = useRef<number>(0); // Ref for accurate timing in callbacks
  
  // GPS data - routeSegments: each segment is a continuous run (no movement during pause)
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [routeSegments, setRouteSegments] = useState<GpsPoint[][]>([[]]);
  const [totalDistance, setTotalDistance] = useState(0); // in meters
  const totalDistanceRef = useRef(0);
  
  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number>(0);
  
  // GPS restart key - only used when a watcher is missing after foregrounding.
  const [gpsRestartKey, setGpsRestartKey] = useState(0);
  
  // GPS watch - watchId for standard Geolocation, bgWatcherId for BackgroundGeolocation
  const watchIdRef = useRef<string | null>(null);
  const bgWatcherIdRef = useRef<string | null>(null);
  const lastPositionRef = useRef<GpsPoint | null>(null);
  const locationPermissionConfirmedRef = useRef(false);
  const lastGpsUpdateRef = useRef<number>(Date.now());
  const runActiveRef = useRef(true);
  const isPausedRef = useRef(false);
  
  // Audio announcements
  const lastAnnouncementKmRef = useRef<number>(0);
  const lastDistanceIntervalRef = useRef<number>(0);
  const audioIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAnnouncedStartRef = useRef<boolean>(false);
  const announceStatsRef = useRef<() => void>(() => {});
  const lastManualAnnounceTimeRef = useRef<number>(0);
  const lastCoachAnnounceTimeRef = useRef<number>(0);
  const lastLiveCoachAtRef = useRef<number>(0);
  const lastCoachSpokenTextRef = useRef<string>('');
  const coachInFlightRef = useRef(false);
  const coachIntroAnnouncedRef = useRef(false);
  const coachAudioReadyRef = useRef(false);
  const coachOpeningDoneRef = useRef(false);
  const [coachArmed, setCoachArmed] = useState(false);

  /** Ignore stale HealthKit HR (e.g. old Watch samples without an active workout). */
  const LIVE_HR_MAX_AGE_MS = 120_000;
  const MIN_HR_SAMPLES_FOR_SUMMARY = 3;
  const MIN_HR_TRACKED_SECONDS = 20;

  const filterHrSamplesToRunWindow = useCallback((samples: HrSample[], runEndMs = Date.now()) => {
    const runStart = startTimeRef.current;
    if (!runStart) return [];
    return samples.filter(
      (s) =>
        s.timestamp >= runStart - 5_000 &&
        s.timestamp <= runEndMs + 5_000 &&
        runEndMs - s.timestamp <= LIVE_HR_MAX_AGE_MS + Math.max(0, runEndMs - runStart),
    );
  }, []);

  // Heart-rate samples streamed from Apple Health while the run is active.
  const hrSamplesRef = useRef<HrSample[]>([]);
  const lastHrAnnounceTimeRef = useRef<number>(0);
  const lastHrFetchEndRef = useRef<number>(0);
  const [currentBpm, setCurrentBpm] = useState<number | null>(null);
  const currentBpmRef = useRef<number | null>(null);
  // Seed from cached "connected" flag so the intro doesn't say "not connected"
  // during the brief moment before the native check returns.
  const [healthKitConnected, setHealthKitConnected] = useState<boolean>(() =>
    getHealthConnectedCacheSync(),
  );

  // Structured plan tracker: drives the "warm up → intervals → cooldown"
  // voice cues whenever Arnold (or the user) provided a multi-phase plan.
  const [activePhaseIndex, setActivePhaseIndex] = useState<number>(-1);
  const announcedEndCueForPhaseRef = useRef<Set<number>>(new Set());
  const phaseStartedAtRef = useRef<number>(0);

  // Settings from RunningStart - load synchronously to prevent audio before settings load
  const [settings, setSettings] = useState<{
    activityType: string;
    audioStats: string;
    audioVoice?: 'female' | 'male';
    gpsActivity?: GpsActivityKind;
    coachGuide?: RunningCoachGuideSettings;
  }>(() => parseRunningSettingsFromStorage(localStorage.getItem('runningSettings')));

  const gpsKind: GpsActivityKind =
    settings.gpsActivity === 'walk' || settings.gpsActivity === 'hike'
      ? settings.gpsActivity
      : 'run';

  // Build the zone table once per (profile age) change.
  const ageYears = profile?.birth_date
    ? Math.max(10, new Date().getFullYear() - new Date(profile.birth_date).getFullYear())
    : null;
  const hrZoneSettings = useMemo(
    () => buildHrZones({ ageYears, maxHr: null }),
    [ageYears]
  );

  // Parse the coach plan into a structured timeline (warmup → intervals →
  // cooldown, with target zones/paces per phase). When the plan can't be
  // parsed we still get a single "free run" phase so the coach has something
  // to talk about.
  const planStructure: ParsedRunStructure = useMemo(() => {
    try {
      return parseRunPlan(normalizeCoachPlanText(settings.coachGuide?.plan));
    } catch (err) {
      console.warn('Failed to parse coach run plan:', err);
      return { phases: [], totalPlannedSeconds: 0, hasStructure: false };
    }
  }, [settings.coachGuide?.plan]);

  const coachModeActive = useMemo(() => {
    const plan = normalizeCoachPlanText(settings.coachGuide?.plan);
    return !!(settings.coachGuide?.enabled && plan.trim());
  }, [settings.coachGuide?.enabled, settings.coachGuide?.plan]);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const planStructureRef = useRef(planStructure);
  planStructureRef.current = planStructure;
  const healthKitConnectedRef = useRef(healthKitConnected);
  healthKitConnectedRef.current = healthKitConnected;
  const runStartedSpeechRef = useRef<() => string>(() => '');

  // Phase-level target zone: derived from the active phase's `targetZone`.
  // Falls back to the plan-wide hint when the phase doesn't specify one.
  const planFallbackZone: HrZoneId | null = useMemo(() => {
    const plan = settings.coachGuide?.plan || '';
    const m = plan.match(/zone\s*([1-5])|\bz([1-5])\b/i);
    if (!m) return null;
    const num = parseInt(m[1] || m[2], 10);
    if (!Number.isFinite(num) || num < 1 || num > 5) return null;
    return (`z${num}` as HrZoneId);
  }, [settings.coachGuide?.plan]);

  const activePhase = activePhaseIndex >= 0 ? planStructure.phases[activePhaseIndex] : null;
  const targetZone: HrZoneId | null = activePhase?.targetZone || planFallbackZone || null;

  const gpsExerciseName = useMemo(() => exerciseNameForGpsActivity(gpsKind), [gpsKind]);
  const gpsWorkoutType = useMemo(() => workoutTypeForGpsActivity(gpsKind), [gpsKind]);

  const defaultTitleWithDate = useCallback(
    (dateStr: string) => {
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkDate', { date: dateStr });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeDate', { date: dateStr });
      return t('running.gpsActivity.titleRunDate', { date: dateStr });
    },
    [gpsKind, t]
  );

  const defaultTitleWithKm = useCallback(
    (km: number) => {
      const d = km.toFixed(2);
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkKm', { distance: d });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeKm', { distance: d });
      return t('running.gpsActivity.titleRunKm', { distance: d });
    },
    [gpsKind, t]
  );

  const workoutDescriptionLine = useCallback(
    (km: number, timeLabel: string) => {
      if (gpsKind === 'walk')
        return t('running.gpsActivity.descWalk', { distance: km, duration: timeLabel });
      if (gpsKind === 'hike')
        return t('running.gpsActivity.descHike', { distance: km, duration: timeLabel });
      return t('running.gpsActivity.descRun', { distance: km, duration: timeLabel });
    },
    [gpsKind, t]
  );
  
  // Run starts immediately when screen loads (no countdown)
  const runActive = isRunning;
  const runEndedRef = useRef(false);
  
  useEffect(() => {
    runActiveRef.current = runActive;
    isPausedRef.current = isPaused;
  }, [runActive, isPaused]);

  useEffect(() => {
    totalDistanceRef.current = totalDistance;
  }, [totalDistance]);

  useEffect(() => {
    currentBpmRef.current = currentBpm;
  }, [currentBpm]);

  // If iOS drops a watcher while the app is backgrounded, recreate it once on return.
  // Do not tear down healthy watchers on every foreground event: that caused repeated
  // permission checks and route gaps.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive || !runActiveRef.current || isPausedRef.current) return;
      const staleMs = Date.now() - lastGpsUpdateRef.current;
      const watchersMissing = !bgWatcherIdRef.current && !watchIdRef.current;
      if (watchersMissing || staleMs > 45_000) {
        console.warn('[GPS] Foreground recovery — restarting watchers', { watchersMissing, staleMs });
        setGpsRestartKey(k => k + 1);
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  // Android: Prompt to disable battery optimization for reliable background tracking
  // (OPPO, Xiaomi, Huawei etc. aggressively suspend apps; disabling helps GPS + TTS continue)
  useEffect(() => {
    if (!runActive || Capacitor.getPlatform() !== 'android') return;
    const check = async () => {
      try {
        const dismissed = localStorage.getItem('straiv_battery_prompt_dismissed');
        if (dismissed === 'true') return;
        const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
        const { enabled } = await BatteryOptimization.isBatteryOptimizationEnabled();
        if (enabled) setShowBatteryPrompt(true);
      } catch (_) {}
    };
    void check();
  }, [runActive]);
  
  // Background persistence - save session periodically
  // Using refs to avoid stale closure issues
  const sessionDataRef = useRef<any>(null);
  
  // Flattened route for backward compat (storage, workout save) - memoized to prevent map flicker when typing
  const routePoints = useMemo(() => routeSegments.flat(), [routeSegments]);
  
  // Update session data ref whenever state changes
  useEffect(() => {
    sessionDataRef.current = {
      isRunning,
      isPaused,
      elapsedSeconds,
      totalDistance,
      routePoints,
      routeSegments,
      pauseCount,
      startTime: startTimeRef.current,
      pausedTime: pausedTimeRef.current,
      lastSplitKm: lastSplitKmRef.current,
      splitTimes: splitTimesRef.current,
      settings,
      timestamp: Date.now()
    };
  }, [isRunning, isPaused, elapsedSeconds, totalDistance, routeSegments, pauseCount, settings]);
  
  // Save session to localStorage periodically (only when run active, not during countdown)
  useEffect(() => {
    if (!runActive) return;
    
    const saveSession = () => {
      if (sessionDataRef.current) {
        sessionDataRef.current.timestamp = Date.now();
        try {
          localStorage.setItem('activeRunningSession', JSON.stringify(sessionDataRef.current));
        } catch (e) {
          console.warn('Failed to save running session:', e);
        }
      }
    };
    
    // Save immediately on start
    saveSession();
    
    // Then save every 5 seconds
    const saveInterval = setInterval(saveSession, 5000);
    return () => clearInterval(saveInterval);
  }, [runActive]);

  // Lock Screen + Dynamic Island Live Activity (iOS 16.2+, no-op elsewhere).
  // Starts as soon as we have an active run, pushes updates whenever stats
  // change (the wrapper throttles internally), and tears down on unmount so
  // the widget disappears reliably even on navigation/back-swipe.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    let cancelled = false;
    void RunLiveActivity.start().then(() => {
      if (cancelled) {
        void RunLiveActivity.end();
      }
    });

    return () => {
      cancelled = true;
      void RunLiveActivity.end();
    };
  }, [runActive]);

  // Push the latest stats into the Lock Screen / Dynamic Island. Pace is
  // computed as overall average so far (mm:ss/km). Pause/resume are forced
  // through with `flush` so the widget reacts instantly.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const distanceKm = totalDistance / 1000;
    const paceSecondsPerKm =
      elapsedSeconds > 0 && distanceKm > 0.01 ? elapsedSeconds / distanceKm : 0;

    const payload = {
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
    };

    void RunLiveActivity.flush(payload);
  }, [runActive, isPaused]);

  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const distanceKm = totalDistance / 1000;
    const paceSecondsPerKm =
      elapsedSeconds > 0 && distanceKm > 0.01 ? elapsedSeconds / distanceKm : 0;

    void RunLiveActivity.update({
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
    });
  }, [elapsedSeconds, totalDistance, isPaused, runActive]);

  // Log screen view on mount
  useEffect(() => {
    Analytics.logScreenView('RunningActive');
  }, []);

  // Background audio keep-alive: prevents Android Doze/WebView from suspending JS when screen is off.
  // iOS: SKIPPED entirely. The silent WAV loop is a WebView <audio> element which iOS surfaces as a
  //   "Safari video" Now Playing tile on the lock screen and also competes for the audio session,
  //   pausing Spotify/Apple Music. On iOS we don't need this hack: BackgroundGeolocation's native
  //   background mode keeps the app alive, and the Live Activity is the lock-screen presence.
  // Android: still needed - silent audio keeps app active so location updates and TTS continue.
  useEffect(() => {
    if (!runActive || !Capacitor.isNativePlatform()) return;
    const isIOS = Capacitor.getPlatform() === 'ios';
    if (isIOS) return; // iOS keep-alive disabled - see comment above
    let audio: HTMLAudioElement | null = null;
    let blobUrl: string | null = null;
    let cancelled = false;
    let pauseHandler: (() => void) | null = null;
    const startAudio = () => {
      if (cancelled) return;
      if (audio && !audio.paused) return; // Already playing
      if (audio && audio.paused) {
        if (isIOS) prepareBackgroundKeepAlive();
        const p = audio.play();
        if (p?.catch) p.catch(() => { if (!cancelled) setTimeout(startAudio, 800); });
        return;
      }
      if (isIOS) prepareBackgroundKeepAlive(); // playback + mixWithOthers - plays when locked, music keeps playing
      try {
        const sampleRate = 44100;
        const numChannels = 1;
        const bitsPerSample = 16;
        const duration = 1;
        const dataSize = sampleRate * numChannels * (bitsPerSample / 8) * duration;
        const buffer = new ArrayBuffer(44 + dataSize);
        const view = new DataView(buffer);
        const writeStr = (offset: number, str: string) => {
          for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
        };
        writeStr(0, 'RIFF');
        view.setUint32(4, 36 + dataSize, true);
        writeStr(8, 'WAVE');
        writeStr(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
        view.setUint16(32, numChannels * (bitsPerSample / 8), true);
        view.setUint16(34, bitsPerSample, true);
        writeStr(36, 'data');
        view.setUint32(40, dataSize, true);
        view.setInt16(44, 1, true);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
        audio = new Audio(blobUrl);
        audio.loop = true;
        audio.volume = 0.01;
        const tryPlay = () => {
          if (!audio || cancelled) return;
          const p = audio.play();
          if (p?.catch) {
            p.catch((e: unknown) => {
              if (!cancelled) {
                console.warn('[Run] Background audio play failed, retrying in 800ms:', e);
                setTimeout(tryPlay, 800);
              }
            });
          }
        };
        tryPlay();
        pauseHandler = () => {
          if (cancelled || !audio) return;
          if (audio.ended) return;
          setTimeout(() => {
            if (cancelled || !audio) return;
            tryPlay();
            console.log('[Run] Background audio restart after pause (interruption)');
          }, 900);
        };
        audio.addEventListener('pause', pauseHandler);
        console.log('[Run] Background audio keep-alive started', isIOS ? '(iOS)' : '(Android)');
        // iOS only: briefly release session so Spotify/Apple Music can resume, then reactivate for keep-alive
        if (isIOS) setTimeout(() => resumeMusicAfterRunStart(), 600);
      } catch (e) {
        console.warn('[Run] Could not start background audio:', e);
        if (!cancelled) setTimeout(startAudio, 500);
      }
    };
    startAudio(); // Start immediately - prepareBackgroundKeepAlive called inside startAudio
    const sub = App.addListener('appStateChange', ({ isActive }) => {
      if (cancelled) return;
      if (!isActive) {
        if (isIOS) prepareBackgroundKeepAlive();
        startAudio(); // Fallback: ensure audio runs when user locks
      }
    });
    return () => {
      cancelled = true;
      sub.then((l) => l.remove());
      if (audio && pauseHandler) audio.removeEventListener('pause', pauseHandler);
      try {
        audio?.pause();
        audio = null;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = null;
        console.log('[Run] Background audio keep-alive stopped');
      } catch {}
    };
  }, [runActive]);

  // Keyboard height for Run Summary - keep Notes visible above keyboard on iOS
  const keyboardListenersRef = useRef<{ remove: () => Promise<void> }[]>([]);
  useEffect(() => {
    if (!showRunSummary || !Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const isIOS = Capacitor.getPlatform() === 'ios';
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
    void Promise.all([
      Keyboard.addListener(showEvent as 'keyboardWillShow', (info: { keyboardHeight: number }) => {
        if (!cancelled) setKeyboardHeight(info.keyboardHeight);
      }),
      Keyboard.addListener(hideEvent as 'keyboardWillHide', () => {
        if (!cancelled) setKeyboardHeight(0);
      }),
    ]).then(([show, hide]) => {
      if (!cancelled) {
        keyboardListenersRef.current = [show, hide];
      } else {
        show.remove();
        hide.remove();
      }
    });
    return () => {
      cancelled = true;
      setKeyboardHeight(0);
      keyboardListenersRef.current.forEach((l) => l.remove());
      keyboardListenersRef.current = [];
    };
  }, [showRunSummary]);

  
  // Restore session from storage on mount (for background persistence)
  useEffect(() => {
    const savedSession = localStorage.getItem('activeRunningSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (Date.now() - session.timestamp < 2 * 60 * 60 * 1000) {
          console.log('[Run] Restoring session from background:', session);
          const segments = session.routeSegments || (session.routePoints?.length ? [session.routePoints] : [[]]);
          setRouteSegments(segments);
          const pts = segments.flat();
          if (pts.length > 0) {
            const last = pts[pts.length - 1];
            setCurrentPosition([last.lat, last.lng]);
            lastPositionRef.current = last;
          }
          setElapsedSeconds(session.elapsedSeconds || 0);
          setTotalDistance(session.totalDistance || 0);
          setPauseCount(session.pauseCount || 0);
          setIsPaused(session.isPaused || false);
          startTimeRef.current = session.startTime || Date.now();
          pausedTimeRef.current = session.pausedTime || 0;
          lastSplitKmRef.current = session.lastSplitKm || 0;
          splitTimesRef.current = session.splitTimes || [];
          if (session.settings) setSettings(session.settings);
          Analytics.runningSessionRestored(session.elapsedSeconds || 0, session.totalDistance || 0);
        } else {
          localStorage.removeItem('activeRunningSession');
          Analytics.runningSessionExpired(Math.round((Date.now() - session.timestamp) / 60000));
          startTimeRef.current = Date.now();
        }
      } catch (e) {
        console.warn('Failed to restore running session:', e);
        startTimeRef.current = Date.now();
      }
    } else {
      startTimeRef.current = Date.now();
    }
  }, []);

  // Routes announcements to the right TTS engine:
  //  - Coach mode  → cloud TTS (OpenAI/ElevenLabs natural voice)
  //  - Normal run  → on-device voice (instant, no cold-start delay)
  type AnnounceKind = 'coach' | 'stats' | 'system';
  const announce = useCallback(async (
    text: string,
    options?: { duckOthers?: boolean; force?: boolean; kind?: AnnounceKind; priority?: boolean },
  ) => {
    if (!audioEnabled || typeof window === 'undefined') return;

    const kind = options?.kind ?? 'stats';
    if (coachModeActive && kind === 'stats') return;
    if (!coachModeActive && kind === 'coach') return;
    if (!coachModeActive && settings.audioStats === 'off' && kind === 'stats' && !options?.force) return;

    const sanitized = String(text || '')
      .replace(/[<>&"'`{}\[\]\\|@#$%^*+=~:;\/]/g, ' ')
      .replace(/[''\u2018\u2019\u201C\u201D]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!sanitized) return;

    const voice = (settings.audioVoice || 'female') as 'male' | 'female';

    try {
      // Coach mode uses queued cloud TTS. Normal run uses on-device TTS
      // directly so cues are instant.
      if (coachModeActive) {
        await speakCoachCue(sanitized, {
          voice,
          useNatural: true,
          priority: options?.priority,
        });
      } else {
        await speakNativeNow(sanitized, voice);
      }
    } catch (err) {
      console.warn('[Audio] announce failed:', err);
    }
  }, [audioEnabled, settings.audioStats, settings.audioVoice, coachModeActive]);
  
  // Helper to convert time to natural speech (e.g., "2 minutes and 30 seconds")
  const formatTimeForSpeech = useCallback((totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const parts: string[] = [];
    
    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }
    
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
  }, []);
  
  // Announce stats with natural, conversational speech
  const announceStats = useCallback(() => {
    const distanceKm = totalDistance / 1000;
    const paceSeconds = elapsedSeconds > 0 && distanceKm > 0 
      ? elapsedSeconds / distanceKm 
      : 0;
    
    // Natural distance announcement
    let distanceText: string;
    if (distanceKm >= 1) {
      const wholeKm = Math.floor(distanceKm);
      const decimal = Math.round((distanceKm - wholeKm) * 100);
      if (decimal === 0) {
        distanceText = `${wholeKm} kilometer${wholeKm !== 1 ? 's' : ''}`;
      } else {
        distanceText = `${wholeKm} point ${decimal < 10 ? 'oh ' : ''}${decimal.toString().split('').join(' ')} kilometers`;
      }
    } else {
      distanceText = `${Math.round(totalDistance)} meters`;
    }
    
    // Natural time announcement (e.g., "12 minutes and 30 seconds")
    const timeText = formatTimeForSpeech(elapsedSeconds);
    
    // Convert pace to spoken format - avoid "0 minutes" (iOS SSML issues)
    const formatPaceForSpeech = (paceInSeconds: number): string => {
      if (!paceInSeconds || !isFinite(paceInSeconds)) return '';
      const mins = Math.floor(paceInSeconds / 60);
      const secs = Math.round(paceInSeconds % 60);
      if (mins === 0) return `${secs} second${secs !== 1 ? 's' : ''}`;
      if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} seconds`;
    };
    
    // Simple format to avoid iOS SSML parse errors (no apostrophes, minimal punctuation)
    let announcement = `You have covered ${distanceText} in ${timeText}`;
    if (paceSeconds > 0 && isFinite(paceSeconds)) {
      announcement += `. Average pace ${formatPaceForSpeech(paceSeconds)} per kilometer`;
    }
    announcement += '.';
    announce(announcement, { kind: 'stats', duckOthers: true });
  }, [totalDistance, elapsedSeconds, announce, formatTimeForSpeech]);
  
  const isTimeBasedInterval = ['1min', '5min', '10min'].includes(settings.audioStats);
  const isDistanceBasedInterval = ['500m', '1km', '5km'].includes(settings.audioStats);
  const distanceIntervalMeters = settings.audioStats === '500m' ? 500 : settings.audioStats === '1km' ? 1000 : settings.audioStats === '5km' ? 5000 : 0;

  // Keep ref updated so interval callback always has latest (avoids resetting interval every second)
  announceStatsRef.current = announceStats;

  // Kilometer milestone announcements - only when using time-based interval (avoid duplicate with distance interval)
  useEffect(() => {
    if (coachModeActive) return;
    if (isDistanceBasedInterval) return;
    const currentKm = Math.floor(totalDistance / 1000);
    if (currentKm > lastAnnouncementKmRef.current && currentKm > 0) {
      lastAnnouncementKmRef.current = currentKm;
      
      // Calculate split pace for this km
      const currentElapsed = elapsedSecondsRef.current;
      const prevSplit = splitTimesRef.current.find(s => s.km === currentKm - 1);
      const splitTime = prevSplit ? currentElapsed - prevSplit.elapsedSeconds : currentElapsed;
      const splitMins = Math.floor(splitTime / 60);
      const splitSecs = splitTime % 60;
      
      // Log kilometer milestone
      Analytics.runningKilometerCompleted(currentKm, splitTime, currentElapsed);
      Analytics.runningAudioAnnouncementPlayed('km_milestone');
      
      // Simple format to avoid iOS SSML parse errors (avoid "0 minutes", complex punctuation)
      let message = `${currentKm} kilometer${currentKm > 1 ? 's' : ''} done`;
      if (splitTime > 0 && splitTime < 1800) {
        if (splitMins > 0) {
          message += ` in ${splitMins} minute${splitMins !== 1 ? 's' : ''}${splitSecs > 0 ? ` and ${splitSecs} seconds` : ''}`;
        } else {
          message += ` in ${splitSecs} seconds`;
        }
      }
      message += '.';
      announce(message, { kind: 'stats', duckOthers: true });
    }
  }, [totalDistance, announce, isDistanceBasedInterval, coachModeActive]);
  
  // Timed audio announcements (every 1/5/10 min) - only when time-based interval
  // Uses ref for announceStats so interval is NOT reset every second (elapsedSeconds changes)
  useEffect(() => {
    if (coachModeActive) return;
    if (!isTimeBasedInterval || settings.audioStats === 'off' || !audioEnabled || isPaused || !runActive) {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
      return;
    }
    
    const intervalMinutes = parseInt(settings.audioStats, 10) || 5;
    const intervalMs = intervalMinutes * 60 * 1000;
    
    audioIntervalRef.current = setInterval(() => {
      if (runActive && !isPaused) {
        // Skip if user recently triggered manual announce (prevents cutting off button-triggered speech)
        if (Date.now() - lastManualAnnounceTimeRef.current < 90000) return;
        announceStatsRef.current();
      }
    }, intervalMs);
    
    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [settings.audioStats, audioEnabled, isPaused, runActive, isTimeBasedInterval, coachModeActive]);
  
  // Distance-based audio announcements (every 500m / 1km / 5km)
  useEffect(() => {
    if (coachModeActive) return;
    if (!isDistanceBasedInterval || distanceIntervalMeters <= 0 || !audioEnabled || isPaused || !runActive) return;
    while (totalDistance >= lastDistanceIntervalRef.current + distanceIntervalMeters) {
      lastDistanceIntervalRef.current += distanceIntervalMeters;
      announceStats();
      Analytics.runningAudioAnnouncementPlayed('stats');
    }
  }, [totalDistance, isDistanceBasedInterval, distanceIntervalMeters, audioEnabled, isPaused, runActive, announceStats, coachModeActive]);

  const runStartedSpeech = useCallback(() => {
    return t(
      gpsKind === 'walk'
        ? 'running.gpsActivity.sessionStarted_walk'
        : gpsKind === 'hike'
          ? 'running.gpsActivity.sessionStarted_hike'
          : 'running.gpsActivity.sessionStarted_run',
    );
  }, [t, gpsKind]);
  runStartedSpeechRef.current = runStartedSpeech;

  // Clear any stale "TTS unavailable" cache when a coached run starts.
  useEffect(() => {
    if (coachModeActive) resetCoachVoiceSession();
  }, [coachModeActive]);

  // Coach mode: rich intro that walks through today's plan, then arms live coach.
  // The intro IS the start announcement — no separate "Run started" cue.
  useEffect(() => {
    if (!coachModeActive || !runActive || !audioEnabled || isPaused) return;
    if (coachOpeningDoneRef.current) return;
    coachOpeningDoneRef.current = true;
    console.log('[RunCoach] Opening sequence starting');

    const runCoachOpeningSequence = async () => {
      const voice = (settingsRef.current.audioVoice || 'female') as 'male' | 'female';
      const coach = settingsRef.current.coachGuide;
      if (!coach?.enabled || !coach.plan.trim()) return;

      // Wait briefly so profile (custom coach name) and HealthKit status are
      // loaded before composing the intro — avoids saying "Arnold" or
      // "Apple Health not connected" when the values are about to arrive.
      const waitStart = Date.now();
      while (Date.now() - waitStart < 1500) {
        const hasName = !!profileRef.current?.ai_coach_name?.trim();
        if (hasName) break;
        await new Promise((r) => setTimeout(r, 80));
      }

      // Get authoritative HealthKit state (native call is fast).
      let hkConnected = healthKitConnectedRef.current;
      if (user?.id) {
        try {
          hkConnected = await Promise.race([
            getHealthConnectedState(user.id),
            new Promise<boolean>((r) => setTimeout(() => r(hkConnected), 1200)),
          ]);
          setHealthKitConnected(hkConnected);
        } catch {
          // keep cached
        }
      }

      const title = profileRef.current?.ai_coach_name?.trim() || 'Arnold';
      const structure = planStructureRef.current;
      const planSummary = buildPlanIntroSpeech(coach.plan, structure);
      const activity = gpsKind === 'walk' ? 'walk' : gpsKind === 'hike' ? 'hike' : 'run';

      let planSentence: string;
      if (planSummary) {
        planSentence = ` Today's ${activity} is: ${planSummary}. I will guide you through every phase.`;
      } else if (coach.plan.trim().length > 8) {
        const shortPlan = coach.plan.trim().slice(0, 220).replace(/[.!?]+\s*$/, '');
        planSentence = ` Today's session: ${shortPlan}. I will be with you the whole way.`;
      } else {
        planSentence = ' I will guide you through this entire session.';
      }

      const hrNote = hkConnected
        ? ' I will track your heart rate from Apple Health to keep you on target.'
        : ' Apple Health is not connected, so I will coach on pace and structure.';

      const introText =
        `And we are off! Hi, I am ${title}, your run coach.` +
        ` I will guide you to reach your workout goals today.` +
        `${planSentence}${hrNote} Let us go!`;

      console.log('[RunCoach] Intro text:', introText);

      // Brief settle so audio session is ready after navigation.
      await new Promise((r) => setTimeout(r, 400));

      const armCoach = () => {
        coachAudioReadyRef.current = true;
        lastLiveCoachAtRef.current = Date.now();
        setCoachArmed(true);
      };
      const armTimeout = window.setTimeout(armCoach, 7000);

      try {
        hasAnnouncedStartRef.current = true;
        coachIntroAnnouncedRef.current = true;
        await speakCoachCue(introText, { voice, useNatural: true, priority: true });
        Analytics.runningAudioAnnouncementPlayed('start');
        Analytics.runningAudioAnnouncementPlayed('coach_guidance');
        lastCoachAnnounceTimeRef.current = Date.now();
        armCoach();
        console.log('[RunCoach] Intro complete, live coach is now armed');
      } finally {
        window.clearTimeout(armTimeout);
        if (!coachAudioReadyRef.current) armCoach();
      }
    };

    void runCoachOpeningSequence().catch((err) => {
      console.error('[RunCoach] Opening sequence failed:', err);
      hasAnnouncedStartRef.current = true;
      coachIntroAnnouncedRef.current = true;
      coachAudioReadyRef.current = true;
      lastLiveCoachAtRef.current = Date.now();
      setCoachArmed(true);
      const voice = (settingsRef.current.audioVoice || 'female') as 'male' | 'female';
      void speakNativeNow('Coach is ready. Let us go.', voice);
    });
  }, [coachModeActive, runActive, audioEnabled, isPaused, gpsKind]);

  // Normal run: fire "Run started" immediately on mount (native voice, no GPS wait).
  useEffect(() => {
    if (coachModeActive || !runActive || !audioEnabled) return;
    if (hasAnnouncedStartRef.current) return;
    if (settings.audioStats === 'off') return;
    hasAnnouncedStartRef.current = true;
    const voice = (settings.audioVoice || 'female') as 'male' | 'female';
    const startedText = runStartedSpeechRef.current() || 'Run started.';
    console.log('[Run] Speaking start cue immediately (native):', startedText);
    void speakNativeNow(startedText, voice);
    Analytics.runningAudioAnnouncementPlayed('start');
  }, [coachModeActive, runActive, audioEnabled, settings.audioStats, settings.audioVoice]);

  // Non-coach: clear any leftover queued cloud TTS from the countdown.
  useEffect(() => {
    if (!runActive || coachModeActive) return;
    stopCoachVoice();
  }, [runActive, coachModeActive]);

  // Personalized Arnold coach — stable interval (30s frequent, 90s when needed, 5m rare).
  useEffect(() => {
    if (!coachModeActive || !settings.coachGuide?.enabled || !settings.coachGuide.plan.trim()) return;
    if (!audioEnabled || isPaused || !runActive) return;

    const coach = settings.coachGuide;
    const tickMs = getCoachMinIntervalSeconds(coach.frequency) * 1000;

    const maybeCoach = () => {
      if (!runActiveRef.current || isPausedRef.current || runEndedRef.current) return;
      if (!coachAudioReadyRef.current || coachInFlightRef.current) return;

      const now = Date.now();
      if (now - lastLiveCoachAtRef.current < tickMs) return;

      const elapsed = elapsedSecondsRef.current;
      if (elapsed < 20) return;

      const structure = planStructureRef.current;
      const distanceKm = totalDistanceRef.current / 1000;
      const averagePaceSecondsPerKm = distanceKm > 0.01 ? elapsed / distanceKm : 0;
      const lastSplit = splitTimesRef.current.at(-1);
      const previousSplit = splitTimesRef.current.at(-2);
      const lastSplitSeconds = lastSplit
        ? lastSplit.elapsedSeconds - (previousSplit?.elapsedSeconds ?? 0)
        : undefined;
      const currentPaceSecondsPerKm = lastSplitSeconds && lastSplitSeconds > 0
        ? lastSplitSeconds
        : averagePaceSecondsPerKm;

      const activeForCoach = getActivePhase(structure, elapsed, distanceKm);
      const phaseCoach = activeForCoach
        ? describeActivePhaseForCoach(activeForCoach, structure)
        : null;
      const targetPaceSecondsPerKm =
        resolveTargetPaceForPhase(activeForCoach?.phase, coach.plan) ??
        parseRunningPlan(coach.plan).targetPaceSecondsPerKm;
      const paceComparison =
        targetPaceSecondsPerKm && currentPaceSecondsPerKm > 0
          ? comparePaceToTarget(currentPaceSecondsPerKm, targetPaceSecondsPerKm)
          : null;
      const paceDeltaSeconds = paceComparison?.deltaSec ?? (
        targetPaceSecondsPerKm
          ? Math.round(currentPaceSecondsPerKm - targetPaceSecondsPerKm)
          : undefined
      );
      const bpm = currentBpmRef.current;
      const snapshot = {
        plan: coach.plan,
        frequency: coach.frequency,
        distanceKm,
        elapsedSeconds: elapsed,
        currentPaceSecondsPerKm,
        averagePaceSecondsPerKm,
        lastSplitSeconds,
        paceDeltaSeconds,
        targetPaceSecondsPerKm,
        paceVerdict: paceComparison?.verdict,
        currentBpm: bpm ?? null,
        targetZone: targetZone ?? null,
        zones: hrZoneSettings.zones.map((z) => ({
          id: z.id,
          label: z.label,
          lowerBpm: z.lowerBpm,
          upperBpm: z.upperBpm,
        })),
        activePhaseLabel: phaseCoach?.label,
        activePhaseSummary: phaseCoach?.summary,
        nextPhaseLabel: phaseCoach?.nextPhaseLabel,
        phaseKind: phaseCoach?.phaseKind,
        remainingPhaseSeconds: activeForCoach?.remainingSeconds ?? undefined,
      };
      const fallback = buildLocalRunningGuidance(snapshot);
      if (!fallback && coach.frequency === 'when_needed') return;

      coachInFlightRef.current = true;
      lastLiveCoachAtRef.current = now;
      requestLiveRunningGuidance(snapshot, fallback)
        .then((guidance) => {
          const text = (guidance || fallback)?.trim();
          if (!text) return;
          const normalized = text.toLowerCase().replace(/\s+/g, ' ');
          if (normalized === lastCoachSpokenTextRef.current) {
            return;
          }
          lastCoachSpokenTextRef.current = normalized;
          lastCoachAnnounceTimeRef.current = Date.now();
          announce(text, { kind: 'coach', duckOthers: true, force: true });
          Analytics.runningAudioAnnouncementPlayed('coach_guidance');
        })
        .finally(() => {
          coachInFlightRef.current = false;
        });
    };

    const id = window.setInterval(maybeCoach, tickMs);
    return () => window.clearInterval(id);
  }, [
    coachModeActive,
    settings.coachGuide?.enabled,
    settings.coachGuide?.plan,
    settings.coachGuide?.frequency,
    audioEnabled,
    isPaused,
    runActive,
    announce,
    targetZone,
    hrZoneSettings.zones,
  ]);
  
  // Detect whether the user has connected Apple Health. Used both to decide
  // whether to poll HR samples and to surface the "HR not available" notice
  // on the coach guide setup screen.
  useEffect(() => {
    if (!user?.id) return;
    void getHealthConnectedState(user.id).then(setHealthKitConnected).catch(() => setHealthKitConnected(false));
  }, [user?.id]);

  // When a run starts, begin HR polling from run start (not an arbitrary 60s window).
  useEffect(() => {
    if (!runActive) return;
    lastHrFetchEndRef.current = Math.max(0, startTimeRef.current - 15_000);
  }, [runActive]);

  // Live heart-rate polling from Apple HealthKit (Straiv native query + capgo fallback).
  // Always attempt on iOS during an active run — don't rely only on the "connected" flag.
  useEffect(() => {
    if (!runActive || isPaused) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

    let cancelled = false;
    const mergeHrSamples = (incoming: HrSample[]) => {
      if (incoming.length === 0) return;
      const runStart = startTimeRef.current;
      const now = Date.now();
      const filtered = incoming.filter(
        (s) =>
          s.timestamp >= runStart - 5_000 &&
          s.timestamp <= now + 2_000 &&
          now - s.timestamp <= LIVE_HR_MAX_AGE_MS,
      );
      if (filtered.length === 0) return;

      const byTs = new Map<number, number>();
      for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
      for (const s of filtered) byTs.set(s.timestamp, s.bpm);
      hrSamplesRef.current = [...byTs.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([timestamp, bpm]) => ({ timestamp, bpm }));
      const latest = hrSamplesRef.current.at(-1);
      if (latest && now - latest.timestamp <= LIVE_HR_MAX_AGE_MS) {
        setCurrentBpm(latest.bpm);
        if (!healthKitConnectedRef.current && hrSamplesRef.current.length >= 2) {
          setHealthKitConnected(true);
        }
      } else if (hrSamplesRef.current.length === 0) {
        setCurrentBpm(null);
      }
    };

    const sampleNow = async () => {
      const end = new Date();
      const start = new Date(Math.max(0, startTimeRef.current - 15_000));
      const now = Date.now();
      try {
        const [latestBeat, windowSamples] = await Promise.all([
          readLatestHeartRateBpm(LIVE_HR_MAX_AGE_MS).catch(() => null),
          readHeartRateSamplesInRange(start, end, 8000),
        ]);
        if (cancelled) return;
        const batch: HrSample[] = windowSamples.filter(
          (s) => now - s.timestamp <= LIVE_HR_MAX_AGE_MS,
        );
        if (latestBeat && now - latestBeat.timestamp <= LIVE_HR_MAX_AGE_MS) {
          batch.push(latestBeat);
        }
        mergeHrSamples(batch);
        lastHrFetchEndRef.current = end.getTime();
        if (batch.length > 0) {
          console.log('[HR] synced', hrSamplesRef.current.length, 'samples (window', windowSamples.length, ')');
        }
      } catch (err) {
        console.warn('[HR] sample read failed', err);
      }
    };

    void sampleNow();
    const id = window.setInterval(() => { void sampleNow(); }, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runActive, isPaused]);

  // Structured plan driver: advances through Arnold's warmup → intervals →
  // cooldown sequence and speaks the start/end cue for each phase. Works
  // regardless of whether HR data is available.
  useEffect(() => {
    if (!runActive || isPaused || !audioEnabled) return;
    if (!coachModeActive) return;
    if (planStructure.phases.length === 0) return;
    if (!coachArmed) return;

    const distanceKm = totalDistance / 1000;
    const active = getActivePhase(planStructure, elapsedSeconds, distanceKm);
    if (!active) return;

    // Phase change → announce start cue (skip phase 0 entirely; intro covered it).
    if (active.phase.index !== activePhaseIndex) {
      setActivePhaseIndex(active.phase.index);
      phaseStartedAtRef.current = Date.now();
      const skipPhaseZero = active.phase.index === 0 && coachIntroAnnouncedRef.current;
      if (!skipPhaseZero) {
        announce(active.phase.startCue, { kind: 'coach', force: true, duckOthers: true });
        lastCoachAnnounceTimeRef.current = Date.now();
        Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      }
      return;
    }

    // Near the end of a time-bounded phase → speak the "wrap up" cue once.
    if (
      active.remainingSeconds != null &&
      active.remainingSeconds <= 15 &&
      active.remainingSeconds > 0 &&
      active.phase.endCue &&
      !announcedEndCueForPhaseRef.current.has(active.phase.index)
    ) {
      announcedEndCueForPhaseRef.current.add(active.phase.index);
      announce(active.phase.endCue, { kind: 'coach', force: true, duckOthers: true });
      lastCoachAnnounceTimeRef.current = Date.now();
      Analytics.runningAudioAnnouncementPlayed('coach_guidance');
    }
  }, [planStructure, elapsedSeconds, totalDistance, activePhaseIndex, audioEnabled, isPaused, runActive, coachModeActive, coachArmed, announce]);

  // Stop any in-flight TTS when the user leaves the screen.
  useEffect(() => {
    return () => {
      stopCoachVoice();
    };
  }, []);

  // Standalone HR cues only when AI coach is off (coach guidance includes HR).
  useEffect(() => {
    if (coachModeActive) return;
    if (!currentBpm || !audioEnabled || isPaused || !runActive) return;
    if (!settings.coachGuide?.enabled) return;
    const now = Date.now();
    if (now - lastHrAnnounceTimeRef.current < 60_000) return;
    const cue = buildHrZoneVoiceCue(currentBpm, hrZoneSettings.zones, targetZone);
    if (!cue) return;
    // Only speak when out of target zone, or every ~3 minutes when on target.
    const onTarget = !targetZone || cue.zone === targetZone;
    if (onTarget && now - lastHrAnnounceTimeRef.current < 180_000) return;
    lastHrAnnounceTimeRef.current = now;
    announce(cue.message, { kind: 'stats', force: true, duckOthers: true });
    Analytics.runningAudioAnnouncementPlayed('coach_guidance');
  }, [currentBpm, audioEnabled, isPaused, runActive, settings.coachGuide?.enabled, targetZone, hrZoneSettings.zones, announce, coachModeActive]);

  // Timer - updates both state and ref for accurate split recording (only when run active post-countdown)
  useEffect(() => {
    if (runActive && !isPaused) {
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTimeRef.current - pausedTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);
        elapsedSecondsRef.current = elapsed; // Keep ref in sync for callbacks
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [runActive, isPaused]);

  // GPS tracking - BackgroundGeolocation on native (works when screen locked), fallback to standard
  useEffect(() => {
    if (!runActive) return;
    const isNative = Capacitor.isNativePlatform();
    let isMounted = true;
    
    const processLocation = (lat: number, lng: number, accuracy: number, timestamp: number) => {
      if (!isMounted || runEndedRef.current) return;
      lastGpsUpdateRef.current = Date.now();
      const newPoint: GpsPoint = {
        lat,
        lng,
        timestamp: timestamp || Date.now(),
        accuracy,
      };
      
      if (newPoint.accuracy) {
        if (newPoint.accuracy <= 5) setGpsSignal('strong');
        else if (newPoint.accuracy <= 15) setGpsSignal('medium');
        else if (newPoint.accuracy <= 30) setGpsSignal('weak');
        else setGpsSignal('searching');
      }
      
      if (newPoint.accuracy && newPoint.accuracy > 30) return;
      
      setCurrentPosition([newPoint.lat, newPoint.lng]);
      
      if (lastPositionRef.current) {
        const dist = calculateDistance(
          lastPositionRef.current.lat,
          lastPositionRef.current.lng,
          newPoint.lat,
          newPoint.lng
        );
        
        if (dist > 3) {
          setTotalDistance(prev => {
            const newTotal = prev + dist;
            const newTotalKm = newTotal / 1000;
            const currentKm = Math.floor(newTotalKm);
            if (currentKm > lastSplitKmRef.current && currentKm > 0) {
              const currentElapsed = Math.floor((Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000);
              lastSplitKmRef.current = currentKm;
              splitTimesRef.current.push({ km: currentKm, elapsedSeconds: currentElapsed });
            }
            return newTotal;
          });
          setRouteSegments(prev => {
            const last = prev[prev.length - 1];
            return [...prev.slice(0, -1), [...last, newPoint]];
          });
          lastPositionRef.current = newPoint;
        }
      }
    };
    
    const startTracking = async () => {
      try {
        const permission = locationPermissionConfirmedRef.current
          ? { location: 'granted' }
          : await Geolocation.checkPermissions();
        if (!isMounted) return;
        if (permission.location !== 'granted') {
          // RunningStart owns the user-facing permission prompt. If this fails,
          // permission was revoked after the pre-run gate.
          return;
        }
        locationPermissionConfirmedRef.current = true;
        
        const quickPosition = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: 3000,
          maximumAge: 30000,
        }).catch(() => null);
        
        if (isMounted && quickPosition) {
          const quickPoint: GpsPoint = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy,
          };
          setCurrentPosition([quickPoint.lat, quickPoint.lng]);
          setGpsSignal('weak');
        }
        
        let initialPosition: Position | null = null;
        try {
          initialPosition = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000,
          });
        } catch (posErr) {
          if (quickPosition && isMounted) {
            const fallback: GpsPoint = {
              lat: quickPosition.coords.latitude,
              lng: quickPosition.coords.longitude,
              timestamp: quickPosition.timestamp,
              accuracy: quickPosition.coords.accuracy,
            };
            const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
            const isResuming = existingSegments.flat().length > 0;
            if (!isResuming) {
              setRouteSegments(prev => (prev.length === 0 || (prev[prev.length - 1]?.length ?? 0) === 0 ? [[fallback]] : [...prev.slice(0, -1), [...(prev[prev.length - 1] || []), fallback]]));
              lastPositionRef.current = fallback;
            } else {
              lastPositionRef.current = lastPositionRef.current ?? existingSegments.flat().at(-1) ?? fallback;
            }
          }
        }
        
        if (!isMounted) return;
        
        // Android 13+: Request notification permission so foreground service notification can show
        // (Required for GPS/splits to work when screen is off - Doze won't suspend us)
        if (isNative && Capacitor.getPlatform() === 'android' && typeof Notification !== 'undefined' && Notification.permission === 'default') {
          try {
            await Notification.requestPermission();
          } catch (_) {}
        }
        
        // When resuming: set lastPositionRef to CURRENT position so we don't add "walk during pause" to route
        const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
        const allPoints = existingSegments.flat();
        const isResuming = allPoints.length > 0;
        
        if (initialPosition) {
          const initialPoint: GpsPoint = {
            lat: initialPosition.coords.latitude,
            lng: initialPosition.coords.longitude,
            timestamp: initialPosition.timestamp,
            accuracy: initialPosition.coords.accuracy,
          };
          setCurrentPosition([initialPoint.lat, initialPoint.lng]);
          if (isResuming) {
            // Keep the previous route point as the distance anchor if we still
            // have it. BackgroundGeolocation should deliver intermediate points;
            // resetting here can hide watcher gaps and create confusing routes.
            lastPositionRef.current = lastPositionRef.current ?? allPoints[allPoints.length - 1] ?? initialPoint;
          } else {
            setRouteSegments(prev => {
              const last = prev[prev.length - 1];
              if (last.length === 0) return [[initialPoint]];
              return [...prev.slice(0, -1), [...last, initialPoint]];
            });
            lastPositionRef.current = initialPoint;
          }
        } else if (isResuming && quickPosition && isMounted) {
          lastPositionRef.current = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy ?? 50,
          };
        } else if (isResuming && allPoints.length > 0) {
          lastPositionRef.current = allPoints[allPoints.length - 1];
        }
        
        // "Run started" is announced by the mount effects (immediate native for
        // normal runs; rich intro for coach runs) — not after GPS lock.
        
        if (isNative && !isPaused) {
          try {
            const bgGeo = getBackgroundGeolocation();
            if (bgGeo) {
              const watcherId = await bgGeo.addWatcher(
                {
                  backgroundMessage: t('running.active.notification.body'),
                  backgroundTitle: t('running.active.notification.title'),
                  requestPermissions: false,
                  distanceFilter: 3,
                  stale: false,
                },
                (location: { latitude: number; longitude: number; accuracy?: number; time?: number } | null, error: any) => {
                  if (error) {
                    // No toast - user passed Always check on RunningStart
                    return console.warn('[GPS] Background error:', error);
                  }
                  if (location && isMounted) {
                    processLocation(location.latitude, location.longitude, location.accuracy ?? 50, location.time ?? Date.now());
                  }
                }
              );
              if (isMounted) {
                bgWatcherIdRef.current = watcherId;
                console.log('[GPS] Background watcher started');
              }
            } else {
              throw new Error('Plugin not available');
            }
          } catch (bgErr: any) {
            const errMsg = bgErr?.message ?? String(bgErr);
            const errCode = bgErr?.code ?? bgErr?.name;
            console.warn('[GPS] Background geolocation unavailable, using standard:', { message: errMsg, code: errCode, err: bgErr });
            // No toast - user passed Always check on RunningStart; fall back to standard Geolocation
            const watchId = await Geolocation.watchPosition(
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
              (position: Position | null, err) => {
                if (err || !position || !isMounted) return;
                processLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy ?? 50, position.timestamp);
              }
            );
            if (isMounted) watchIdRef.current = watchId;
          }
        } else {
          const watchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
            (position: Position | null, err) => {
              if (err || !position || !isMounted) return;
              if (isPaused) return;
              processLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy ?? 50, position.timestamp);
            }
          );
          if (isMounted) watchIdRef.current = watchId;
        }
      } catch (error: any) {
        console.error('[GPS] Failed to start tracking:', error);
        if (isMounted) toast.error(t('running.active.toast.gpsError', { message: error.message || 'Unknown error' }));
      }
    };
    
    if (runActive && !isPaused) {
      startTracking();
    }
    
    const staleWatchdog = window.setInterval(() => {
      if (!isMounted || isPaused) return;
      if (Date.now() - lastGpsUpdateRef.current > 45_000) {
        console.warn('[GPS] Stale location data — restarting GPS watchers');
        lastGpsUpdateRef.current = Date.now();
        setGpsRestartKey((k) => k + 1);
      }
    }, 15_000);

    return () => {
      isMounted = false;
      window.clearInterval(staleWatchdog);
      if (bgWatcherIdRef.current) {
        getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
        bgWatcherIdRef.current = null;
      }
      if (watchIdRef.current) {
        Geolocation.clearWatch({ id: watchIdRef.current }).catch(console.warn);
        watchIdRef.current = null;
      }
    };
  }, [runActive, isPaused, announce, navigate, gpsRestartKey, t, gpsKind]);
  
  // Track pause duration for analytics
  const pauseStartTimeRef = useRef<number>(0);
  
  // Handle pause - start new segment so movement during pause is not added to route
  const handlePause = () => {
    if (isLocked) return;
    
    const newPauseCount = pauseCount + 1;
    setIsPaused(true);
    setPauseCount(newPauseCount);
    pausedTimeRef.current = Date.now() - startTimeRef.current - (elapsedSeconds * 1000);
    pauseStartTimeRef.current = Date.now(); // Track when pause started for analytics
    // Stop any in-flight coach audio so "Paused" lands instantly, then speak
    // via on-device voice (no cloud round-trip = no delay).
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionPaused'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Start new segment - movement during pause will go here but we clear it on resume
    setRouteSegments(prev => [...prev, []]);
    
    // Log pause event
    Analytics.runningWorkoutPaused(elapsedSeconds, totalDistance, newPauseCount);
    Analytics.runningAudioAnnouncementPlayed('pause');
    
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    if (bgWatcherIdRef.current) {
      getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
      bgWatcherIdRef.current = null;
    }
  };
  
  // Handle resume - empty segment from pause will receive new run points (no line from pause→resume)
  const handleResume = () => {
    if (isLocked || !isRunning || runEndedRef.current) return;
    
    const pauseDuration = pauseStartTimeRef.current > 0 
      ? Math.round((Date.now() - pauseStartTimeRef.current) / 1000)
      : 0;
    
    setIsPaused(false);
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionResumed'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Log resume event
    Analytics.runningWorkoutResumed(elapsedSeconds, totalDistance, pauseDuration);
    Analytics.runningAudioAnnouncementPlayed('resume');
  };
  
  // Track when stop dialog opened for paused time calculation
  const stopDialogOpenedRef = useRef<number>(0);
  
  // Handle stop button - show confirmation and pause timer
  const handleStopClick = () => {
    if (isLocked) return;
    // Save the time when dialog opened
    stopDialogOpenedRef.current = Date.now();
    // Pause the timer while showing confirmation
    setIsPaused(true);
    setShowStopConfirm(true);
    
    // Log stop dialog opened
    Analytics.runningStopDialogOpened();
  };
  
  // Cancel stop - resume the run and account for paused time
  const handleCancelStop = () => {
    if (runEndedRef.current || stopConfirmedRef.current) return;
    
    setShowStopConfirm(false);
    // Add the time spent in dialog to paused time
    if (stopDialogOpenedRef.current > 0) {
      const dialogTime = Date.now() - stopDialogOpenedRef.current;
      pausedTimeRef.current += dialogTime;
      stopDialogOpenedRef.current = 0;
    }
    setIsPaused(false);
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionResumed'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Log stop dialog cancelled
    Analytics.runningStopDialogCancelled();
  };
  
  // Confirm stop and finish workout
  const handleConfirmStop = async () => {
    if (runEndedRef.current) return;
    runEndedRef.current = true;
    stopConfirmedRef.current = true;
    runActiveRef.current = false;

    const stopElapsed = elapsedSecondsRef.current;
    const stopDistanceM = totalDistanceRef.current;

    Analytics.runningStopDialogConfirmed();
    Analytics.runningWorkoutStopped(stopElapsed, stopDistanceM, true);

    void RunLiveActivity.end();
    void stopCoachVoice();

    setShowStopConfirm(false);
    setIsStoppingRun(true);
    setIsRunning(false);
    setIsPaused(true);

    if (bgWatcherIdRef.current) {
      getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
      bgWatcherIdRef.current = null;
    }
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && startTimeRef.current > 0) {
        try {
          const start = new Date(startTimeRef.current - 10_000);
          const end = new Date();
          const backfill = await readHeartRateSamplesInRange(start, end, 12_000);
          if (backfill.length > 0) {
            const runEnd = end.getTime();
            const filtered = backfill.filter(
              (s) =>
                s.timestamp >= startTimeRef.current - 5_000 &&
                s.timestamp <= runEnd + 5_000,
            );
            const byTs = new Map<number, number>();
            for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
            for (const s of filtered) byTs.set(s.timestamp, s.bpm);
            hrSamplesRef.current = [...byTs.entries()]
              .sort((a, b) => a[0] - b[0])
              .map(([timestamp, bpm]) => ({ timestamp, bpm }));
          }
        } catch (err) {
          console.warn('[HR] end-of-run backfill failed', err);
        }
      }

      const distanceKm = stopDistanceM / 1000;
    const paceSeconds = stopElapsed > 0 && distanceKm > 0 
      ? stopElapsed / distanceKm 
      : 0;
    const durationMinutes = Math.round(stopElapsed / 60);
    
    // Calculate calories using MET formula based on user weight
    // Running MET ≈ 9.8 (moderate pace), adjusted by speed
    // Formula: Calories = MET × weight(kg) × time(hours)
    const userWeightKg = profile?.weight_kg || 70; // Default 70kg if not set
    const avgSpeedKmh = stopElapsed > 0 
      ? distanceKm / (stopElapsed / 3600)
      : 0;
    
    // MET: running pace bands; walking/hiking use lower bases (ACSM-style approximations)
    let met = 8.0;
    if (gpsKind === 'walk') {
      if (avgSpeedKmh >= 6.5) met = 5.0;
      else if (avgSpeedKmh >= 5) met = 3.8;
      else met = 3.3;
    } else if (gpsKind === 'hike') {
      if (avgSpeedKmh >= 5.5) met = 9.0;
      else if (avgSpeedKmh >= 4) met = 7.0;
      else met = 6.0;
    } else {
      if (avgSpeedKmh >= 14) met = 13.5;
      else if (avgSpeedKmh >= 12) met = 11.5;
      else if (avgSpeedKmh >= 10) met = 9.8;
      else if (avgSpeedKmh >= 8) met = 8.3;
      else if (avgSpeedKmh >= 6) met = 6.0;
    }
    
    const timeHours = stopElapsed / 3600;
    const estimatedCalories = Math.round(met * userWeightKg * timeHours);
    
    // Create comprehensive workout summary for Arnold (matching Chat.tsx expected format)
    const summary = {
      type: 'running',
      title: defaultTitleWithKm(distanceKm),
      duration: durationMinutes,
      distance: parseFloat(distanceKm.toFixed(2)),
      pace: formatPace(paceSeconds),
      // Notes will be added when user submits (can include user notes)
      notes: '',
      // Extended data for better analysis
      averageSpeedKmh: avgSpeedKmh.toFixed(1),
      estimatedCalories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsRecorded: routePoints.length,
      userWeight: userWeightKg,
      userAge: profile?.birth_date ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear() : undefined,
      userHeight: profile?.height_cm,
      // Heart-rate zone summary for after-run screen + Arnold analysis.
      heartRate: (() => {
        const runEnd = Date.now();
        let hr = filterHrSamplesToRunWindow(hrSamplesRef.current, runEnd);
        if (hr.length === 0) return null;
        const buckets = bucketHrZones(hr, hrZoneSettings.zones);
        if (buckets.totalSeconds < MIN_HR_TRACKED_SECONDS && hr.length < MIN_HR_SAMPLES_FOR_SUMMARY) {
          return null;
        }
        const bpms = hr.map((s) => s.bpm);
        return {
          maxHr: hrZoneSettings.maxHr,
          maxHrSource: hrZoneSettings.source,
          targetZone,
          avgBpm: Math.round(bpms.reduce((a, b) => a + b, 0) / bpms.length),
          minBpm: Math.min(...bpms),
          peakBpm: Math.max(...bpms),
          sampleCount: hr.length,
          samples: hr,
          zones: hrZoneSettings.zones,
          buckets,
        };
      })(),
      startTime: new Date(startTimeRef.current).toISOString(),
      endTime: new Date().toISOString(),
      startLocation: routePoints.length > 0 ? {
        lat: routePoints[0].lat,
        lng: routePoints[0].lng
      } : null,
      endLocation: routePoints.length > 0 ? {
        lat: routePoints[routePoints.length - 1].lat,
        lng: routePoints[routePoints.length - 1].lng
      } : null,
      _ts: Date.now() // Timestamp for freshness check
    };
    
    setWorkoutSummary(summary);
    
    // Calculate km splits with accurate pace per kilometer
    const splits: { km: number; time: string; pace: string; isPartial?: boolean }[] = [];
    let prevSeconds = 0;
    
    // Get the number of full kilometers completed
    const fullKilometers = Math.floor(distanceKm);
    
    console.log('[Splits] Calculating - distanceKm:', distanceKm, 'fullKilometers:', fullKilometers, 'splitTimesRef:', splitTimesRef.current);
    
    // Add recorded splits (full kilometers completed)
    for (const split of splitTimesRef.current) {
      const splitSeconds = split.elapsedSeconds - prevSeconds;
      const pacePerKm = formatPace(splitSeconds); // seconds per km
      splits.push({
        km: split.km,
        time: formatTime(splitSeconds), // Time for THIS split segment
        pace: pacePerKm
      });
      prevSeconds = split.elapsedSeconds;
    }
    // Ensure every full km is shown (fill gaps if any splits were missed)
    for (let km = 1; km <= fullKilometers; km++) {
      if (!splits.some(s => s.km === km)) {
        const avgPace = distanceKm > 0 ? stopElapsed / distanceKm : 0;
        splits.push({ km, time: formatTime(Math.round(avgPace)), pace: formatPace(avgPace) });
      }
    }
    splits.sort((a, b) => a.km - b.km);
    
    // Calculate the partial/remaining distance after the last full km
    const remainingKm = distanceKm - fullKilometers;
    const lastRecordedSplitTime = splits.length > 0 ? prevSeconds : 0;
    const remainingSeconds = stopElapsed - lastRecordedSplitTime;
    
    console.log('[Splits] Remaining - remainingKm:', remainingKm, 'remainingSeconds:', remainingSeconds);
    
    // Add the partial kilometer if there's remaining distance (more than 10 meters)
    if (remainingKm >= 0.01 && remainingSeconds > 0) {
      // Extrapolate pace to per-km for comparison
      const estimatedPacePerKm = remainingSeconds / remainingKm;
      splits.push({
        km: Math.round(remainingKm * 100) / 100, // Round to 2 decimal places (e.g., 0.49)
        time: formatTime(remainingSeconds),
        pace: formatPace(estimatedPacePerKm),
        isPartial: true
      });
    } else if (splits.length === 0 && distanceKm > 0) {
      const avgPace = stopElapsed / distanceKm;
      splits.push({
        km: Math.round(distanceKm * 100) / 100,
        time: formatTime(stopElapsed),
        pace: formatPace(avgPace),
        isPartial: true
      });
    }
    
    console.log('[Splits] Final result:', splits);
    setKmSplits(splits);
    
    // Set default run title with date
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    setRunTitle(defaultTitleWithDate(dateStr));
    
    // Reset feeling emoji
    setFeelingEmoji("");
    setRunNotes("");
    
    // Natural speech for run summary (use "point" not "dot" for decimals)
    let distanceSpeech: string;
    if (distanceKm >= 1) {
      const wholeKm = Math.floor(distanceKm);
      const decimal = Math.round((distanceKm - wholeKm) * 100);
      distanceSpeech = decimal === 0
        ? `${wholeKm} kilometer${wholeKm !== 1 ? 's' : ''}`
        : `${wholeKm} point ${decimal < 10 ? 'oh ' : ''}${decimal.toString().split('').join(' ')} kilometers`;
    } else {
      distanceSpeech = `${Math.round(stopDistanceM)} meters`;
    }
    {
      const finishText = t(
        gpsKind === 'walk'
          ? 'running.gpsActivity.finishSpeech_walk'
          : gpsKind === 'hike'
            ? 'running.gpsActivity.finishSpeech_hike'
            : 'running.gpsActivity.finishSpeech_run',
        {
          distance: distanceSpeech,
          time: formatTimeForSpeech(stopElapsed),
        },
      );
      void speakNativeNow(finishText, (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    }
    Analytics.runningAudioAnnouncementPlayed('finish');
    
    Analytics.runningWorkoutCompleted({
      distanceKm: parseFloat(distanceKm.toFixed(2)),
      durationSeconds: stopElapsed,
      avgPace: formatPace(paceSeconds),
      calories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsCount: routePoints.length,
      splitsCount: splits.length
    });
    
    Analytics.workoutCompleted('running', {
      duration_minutes: durationMinutes,
      distance_km: distanceKm,
      is_gps_tracked: true,
      pause_count: pauseCount,
      calories: estimatedCalories
    });
    
    Analytics.runningSummaryOpened(distanceKm, stopElapsed);
    } catch (err) {
      console.error('[Run] Failed to build run summary:', err);
      const fallbackKm = stopDistanceM / 1000;
      setWorkoutSummary({
        type: 'running',
        title: defaultTitleWithKm(fallbackKm),
        duration: Math.round(stopElapsed / 60),
        distance: parseFloat(fallbackKm.toFixed(2)),
        pace: '--:--',
        notes: '',
        estimatedCalories: 0,
        pauseCount,
        gpsPointsRecorded: routePoints.length,
        _ts: Date.now(),
      });
      setKmSplits([]);
    } finally {
      setIsStoppingRun(false);
      setShowRunSummary(true);
    }
  };
  
  // Feeling icons (using Lucide icons instead of emojis)
  const feelingOptions = useMemo(() => [
    { id: 'exhausted', icon: Frown, label: t('running.summary.feeling.exhausted'), color: 'text-red-400' },
    { id: 'tough', icon: Meh, label: t('running.summary.feeling.tough'), color: 'text-orange-400' },
    { id: 'good', icon: Smile, label: t('running.summary.feeling.good'), color: 'text-yellow-400' },
    { id: 'strong', icon: Zap, label: t('running.summary.feeling.strong'), color: 'text-primary' },
    { id: 'amazing', icon: Flame, label: t('running.summary.feeling.amazing'), color: 'text-primary' },
  ], [t]);
  
  // Handle save run summary (step 1 → step 2) - also save to workout history
  const handleSaveRunSummary = async () => {
    const runSaveSessionKey = `straiv_run_saved_${startTimeRef.current}`;

    // Already saved this run — advance without creating another workout
    if (savedWorkoutIdRef.current) {
      setShowRunSummary(false);
      Analytics.runningAnalysisPromptShown();
      setShowAnalyzePrompt(true);
      return;
    }

    try {
      const existingId = sessionStorage.getItem(runSaveSessionKey);
      if (existingId) {
        savedWorkoutIdRef.current = existingId;
        setShowRunSummary(false);
        Analytics.runningAnalysisPromptShown();
        setShowAnalyzePrompt(true);
        return;
      }
    } catch {
      /* sessionStorage unavailable */
    }

    if (runSaveInFlightRef.current) return;
    runSaveInFlightRef.current = true;
    setIsSavingRun(true);

    // Log summary saved with details
    const todayTitle = defaultTitleWithDate(
      new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    );
    Analytics.runningSummarySaved(
      runNotes.trim().length > 0,
      feelingEmoji.length > 0,
      runTitle.trim() !== todayTitle
    );

    // Clear the active running session from localStorage
    localStorage.removeItem('activeRunningSession');

    let saveSucceeded = false;

    try {
      const workoutTitle =
        runTitle.trim() ||
        defaultTitleWithKm(Number(workoutSummary?.distance) || 0);
      const workoutNotes = [
        runNotes.trim(),
        feelingEmoji ? `Feeling: ${feelingOptions.find(f => f.id === feelingEmoji)?.label || ''}` : '',
        kmSplits.length > 0 ? `Splits: ${kmSplits.map(s => `${s.km}km: ${s.pace}/km`).join(', ')}` : ''
      ].filter(Boolean).join('\n');

      const { data: workout, error: workoutError } = await createWorkout({
        title: workoutTitle,
        description: workoutDescriptionLine(
          Number(workoutSummary?.distance) || 0,
          formatTime(elapsedSeconds)
        ),
        workout_type: gpsWorkoutType,
        duration_minutes: Math.round(elapsedSeconds / 60),
        calories_burned: workoutSummary?.estimatedCalories || 0,
        difficulty_level: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Moderate',
        notes: workoutNotes,
        started_at: new Date(startTimeRef.current).toISOString(),
        completed_at: new Date().toISOString(),
      });

      if (workoutError || !workout?.id) {
        console.error('Error creating workout:', workoutError);
        toast.error(t('running.summary.toast.saveFailedConnection'));
        runSaveInFlightRef.current = false;
        setIsSavingRun(false);
        return;
      }

      const { error: exerciseError } = await addExerciseToWorkout(workout.id, {
        name: gpsExerciseName,
        muscle_groups: ['legs', 'cardiovascular'],
        category: 'Cardio',
        duration_seconds: Math.max(1, elapsedSeconds),
        distance_km: workoutSummary?.distance ?? 0,
        notes: JSON.stringify({
          pace: workoutSummary?.pace,
          splits: kmSplits,
          routePoints: routePoints.length > 0 ? (() => {
            const maxPoints = 200;
            if (routePoints.length <= maxPoints) return routePoints;
            const step = (routePoints.length - 1) / (maxPoints - 1);
            const sampled: typeof routePoints = [];
            for (let i = 0; i < maxPoints - 1; i++) {
              sampled.push(routePoints[Math.round(i * step)]);
            }
            sampled.push(routePoints[routePoints.length - 1]);
            return sampled;
          })() : [],
          pauseCount: pauseCount,
          avgSpeedKmh: workoutSummary?.averageSpeedKmh,
          feeling: feelingEmoji,
          heartRate: workoutSummary?.heartRate
            ? {
                maxHr: workoutSummary.heartRate.maxHr,
                maxHrSource: workoutSummary.heartRate.maxHrSource,
                targetZone: workoutSummary.heartRate.targetZone,
                avgBpm: workoutSummary.heartRate.avgBpm,
                minBpm: workoutSummary.heartRate.minBpm,
                peakBpm: workoutSummary.heartRate.peakBpm,
                zones: workoutSummary.heartRate.zones.map((z: any) => ({
                  id: z.id,
                  label: z.label,
                  lowerBpm: z.lowerBpm,
                  upperBpm: z.upperBpm,
                })),
                buckets: workoutSummary.heartRate.buckets,
              }
            : null,
        }),
      });

      if (exerciseError) {
        console.error('Error adding run details:', exerciseError);
        await deleteWorkout(workout.id).catch(() => undefined);
        toast.error(t('running.summary.toast.saveDetailsFailed'));
        runSaveInFlightRef.current = false;
        setIsSavingRun(false);
        return;
      }

      saveSucceeded = true;
      savedWorkoutIdRef.current = workout.id;
      try {
        sessionStorage.setItem(runSaveSessionKey, workout.id);
      } catch {
        /* sessionStorage unavailable */
      }
      console.log('Workout saved to history:', workout.id);
      toast.success(t('running.summary.toast.saveSuccess'));
    } catch (error) {
      console.error('Error saving workout to history:', error);
      toast.error(t('running.summary.toast.saveGenericError'));
      runSaveInFlightRef.current = false;
      setIsSavingRun(false);
      return;
    }

    runSaveInFlightRef.current = false;
    setIsSavingRun(false);
    if (!saveSucceeded) return;

    setShowRunSummary(false);
    Analytics.runningAnalysisPromptShown();
    setShowAnalyzePrompt(true);
  };
  
  // Handle delete run - show confirmation first
  const handleDeleteRun = () => {
    setShowDeleteConfirm(true);
  };
  
  // Confirm delete run
  const confirmDeleteRun = () => {
    setShowDeleteConfirm(false);
    setShowRunSummary(false);
    toast.success(t('running.summary.toast.runDeleted'));
    
    // Log run deleted
    Analytics.runningSummaryDeleted();
    navigate('/workouts');
  };
  
  // Handle analyze with Arnold
  const handleAnalyzeWithArnold = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis requested
    Analytics.runningAnalysisRequested(
      workoutSummary?.distance || 0,
      elapsedSeconds
    );
    
    // Build splits string for Arnold
    const splitsText = kmSplits.length > 0 
      ? kmSplits.map(s => `${s.km}km: ${s.pace}/km`).join(', ')
      : 'No splits recorded';
    
    // Add all data to the summary for Arnold
    const heartRate = workoutSummary?.heartRate ?? null;
    const heartRateCoachText = formatHeartRateSummaryForCoach(heartRate);

    const summaryWithAllData = {
      ...workoutSummary,
      _ts: Date.now(),
      type: 'running' as const,
      title:
        runTitle.trim() ||
        defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
      notes: runNotes.trim() || '',
      feeling: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Not specified',
      feelingId: feelingEmoji,
      splits: splitsText,
      kmSplits: kmSplits,
      userNotes: runNotes.trim(),
      heartRate,
      heartRateCoachText,
    };

    if (user?.id && heartRateCoachText) {
      try {
        localStorage.setItem(
          'arnoldLastRunSummary',
          JSON.stringify({
            userId: user.id,
            _ts: Date.now(),
            title: summaryWithAllData.title,
            coachText: heartRateCoachText.trim(),
          }),
        );
      } catch {
        /* ignore */
      }
    }
    
    // Store the workout summary for Arnold to analyze
    try {
      localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(summaryWithAllData));
      console.log('Saved running analysis to localStorage:', summaryWithAllData);
    } catch (e) {
      console.warn('Failed to save workout analysis to localStorage:', e);
    }
    
    // Navigate to chat with Arnold
    navigate('/chat', {
      state: {
        fromWorkout: true,
        workoutData: summaryWithAllData,
      },
    });
  };
  
  // Handle skip analysis
  const handleSkipAnalysis = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis skipped
    Analytics.runningAnalysisSkipped();
    
    toast.success(t('running.summary.toast.skipAnalysis'));
    navigate('/workouts');
  };
  
  // Calculate current pace
  const distanceKm = totalDistance / 1000;
  const currentPaceSeconds = elapsedSeconds > 0 && distanceKm > 0 
    ? elapsedSeconds / distanceKm 
    : 0;
  
  // Calculate real-time calories using MET formula
  const userWeightKg = profile?.weight_kg || 70;
  const avgSpeedKmh = elapsedSeconds > 0 ? distanceKm / (elapsedSeconds / 3600) : 0;
  let currentMet = 6.0;
  if (gpsKind === 'walk') {
    if (avgSpeedKmh >= 6.5) currentMet = 5.0;
    else if (avgSpeedKmh >= 5) currentMet = 3.8;
    else currentMet = 3.3;
  } else if (gpsKind === 'hike') {
    if (avgSpeedKmh >= 5.5) currentMet = 9.0;
    else if (avgSpeedKmh >= 4) currentMet = 7.0;
    else currentMet = 6.0;
  } else {
    if (avgSpeedKmh >= 14) currentMet = 13.5;
    else if (avgSpeedKmh >= 12) currentMet = 11.5;
    else if (avgSpeedKmh >= 10) currentMet = 9.8;
    else if (avgSpeedKmh >= 8) currentMet = 8.3;
  }
  const currentCalories = Math.round(currentMet * userWeightKg * (elapsedSeconds / 3600));
  
  // Route data for display - memoized so map markers don't flicker when runTitle/runNotes change
  const routeSegmentsForMap = useMemo<[number, number][][]>(() =>
    routeSegments
      .filter(seg => seg.length >= 2)
      .map(seg => seg.map(p => [p.lat, p.lng] as [number, number])),
    [routeSegments]
  );
  const routeLine = useMemo<[number, number][]>(() =>
    routePoints.map(p => [p.lat, p.lng]),
    [routePoints]
  );
  // Stable center for summary map - prevents PlatformMap re-render when typing
  const mapCenterForSummary = useMemo<[number, number] | null>(() =>
    routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null,
    [routeLine]
  );
  const coloredRouteSegmentsForSummary = useMemo(
    () => {
      const hr = workoutSummary?.heartRate;
      if (!hr?.samples?.length || routePoints.length < 2) return [];
      return buildZoneColoredRouteSegments(routePoints, hr.samples, hr.zones);
    },
    [workoutSummary?.heartRate, routePoints],
  );
  
  // Auto-follow: Update map center when user position changes (if auto-follow is on)
  useEffect(() => {
    if (currentPosition && isMapCentered) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
  }, [currentPosition, isMapCentered]);
  
  // Handle recenter map - go back to user's position and enable auto-follow
  const handleRecenterMap = () => {
    if (currentPosition) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
    setIsMapCentered(true); // Enable auto-follow, hide recenter button
    Analytics.runningMapRecentered();
  };
  
  // Handle map interaction (user scrolled/panned away) - disable auto-follow
  const handleMapInteraction = useCallback(() => {
    setIsMapCentered(false); // Disable auto-follow, show recenter button
    Analytics.runningMapScrolled();
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-background flex flex-col"
      style={showRunSummary ? { pointerEvents: 'none' } : undefined}
    >
      {/* Map Background */}
      <div className="absolute inset-0 z-0">
        {!currentPosition ? (
          <div className="h-full w-full flex items-center justify-center bg-background">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-primary mx-auto animate-pulse" />
              <div className="text-lg text-primary mt-4">{t('running.active.gpsAcquiring')}</div>
              <div className="text-sm text-muted-foreground mt-2">{t('running.active.pleaseWait')}</div>
            </div>
          </div>
        ) : (
          <PlatformMap 
            center={mapCenter || currentPosition}
            userLocation={currentPosition}
            routeSegments={routeSegmentsForMap}
            showUserMarker={true}
            showRoute={true}
            className="w-full h-full"
            darkMode={true}
            autoFollow={isMapCentered}
            onUserScroll={handleMapInteraction}
          />
        )}
        
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70 pointer-events-none" />
      </div>
      
      {/* Top Stats Bar */}
      <div 
        className="relative z-10 px-4"
        style={{ paddingTop: `calc(${SAFE_AREA.top} + 70px)` }}
      >
        <div className="bg-background/90 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-border/30">
          {/* Timer - Large */}
          <div className="text-center mb-4">
            <div className="text-5xl font-mono font-bold text-foreground">
              {formatTime(elapsedSeconds)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {isPaused ? t('running.active.statusPaused') : t('running.active.statusRunning')}
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Distance */}
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {distanceKm.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground uppercase">km</div>
            </div>
            
            {/* Pace */}
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {formatPace(currentPaceSeconds)}
              </div>
              <div className="text-xs text-muted-foreground uppercase">/km</div>
            </div>
            
            {/* Calories (estimated using MET formula) */}
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {currentCalories}
              </div>
              <div className="text-xs text-muted-foreground uppercase">kcal</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Spacer to push controls to bottom */}
      <div className="flex-1" />
      
      {/* Bottom Controls */}
      <div 
        className="relative z-10 px-4 pb-4"
        style={{ paddingBottom: `calc(${SAFE_AREA.bottom} + 100px)` }}
      >
        {/* Quick Actions - Lock and GPS Signal aligned at bottom */}
        <div className="flex justify-between items-end mb-4">
          {/* Lock Button - aligned with GPS signal */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const newLockedState = !isLocked;
              setIsLocked(newLockedState);
              if (newLockedState) {
                Analytics.runningScreenLocked();
              } else {
                Analytics.runningScreenUnlocked();
              }
            }}
            className="h-10 w-10 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30"
          >
            {isLocked ? (
              <Lock className="h-4 w-4 text-destructive" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
          </Button>
          
          {/* GPS Signal + Re-center button stacked vertically */}
          <div className="flex flex-col items-center gap-2">
            {/* Re-center button - only visible when user has scrolled away */}
            {currentPosition && !isMapCentered && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRecenterMap}
                className="h-10 w-10 rounded-full bg-primary/90 backdrop-blur-md shadow-lg border border-border/30 animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <Navigation className="h-4 w-4 text-primary-foreground" />
              </Button>
            )}
            
            {/* GPS Signal Strength */}
            <div className="h-10 px-3 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30 flex items-center">
              <div className="flex items-end gap-0.5 h-4">
                {[1, 2, 3, 4].map((bar) => {
                  const bars = gpsSignal === 'strong' ? 4 : gpsSignal === 'medium' ? 3 : gpsSignal === 'weak' ? 2 : 1;
                  return (
                    <div
                      key={bar}
                      className={`w-1 rounded-sm transition-all ${
                        bar <= bars ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                      style={{ height: `${bar * 3 + 2}px` }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Controls */}
        <div className="flex items-center justify-center gap-6">
          {/* Stop Button */}
          <Button
            onClick={handleStopClick}
            disabled={isLocked || !isRunning || isStoppingRun}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked ? 'opacity-50' : 'border-destructive text-destructive hover:bg-destructive/10'
            }`}
          >
            <Square className="h-6 w-6" />
          </Button>
          
          {/* Pause/Resume Button */}
          <Button
            onClick={isPaused ? handleResume : handlePause}
            disabled={isLocked || !isRunning || isStoppingRun}
            className={`h-20 w-20 rounded-full shadow-glow ${
              isLocked ? 'opacity-50 bg-muted' : 'bg-gradient-primary'
            }`}
          >
            {isPaused ? (
              <Play className="h-8 w-8 text-black ml-1" />
            ) : (
              <Pause className="h-8 w-8 text-black" />
            )}
          </Button>
          
          {/* Announce Stats Button */}
          <Button
            onClick={() => {
              lastManualAnnounceTimeRef.current = Date.now();
              Analytics.runningStatsAnnouncementRequested();
              Analytics.runningAudioAnnouncementPlayed('stats');
              announceStats();
            }}
            disabled={isLocked || !audioEnabled}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked || !audioEnabled ? 'opacity-50' : 'border-primary text-primary hover:bg-primary/10'
            }`}
          >
            <Volume2 className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Lock indicator */}
        {isLocked && (
          <div className="text-center mt-4 text-sm text-muted-foreground">
            {t('running.active.screenLockedHint')}
          </div>
        )}
      </div>
      
      {/* Stopping overlay — blocks Play/Stop while finishing the run */}
      {isStoppingRun && createPortal(
        <div
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center bg-background/95"
          style={{
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-base font-medium text-foreground">Finishing run…</p>
        </div>,
        document.body
      )}

      {/* Stop Confirmation Dialog - rendered in portal so it blocks ALL interaction including BottomNav */}
      {showStopConfirm && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('running.active.stopDialog.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('running.active.stopDialog.description')}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="outline" onClick={handleCancelStop} className="w-full h-11">
                  {t('running.active.stopDialog.continue')}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleConfirmStop}
                  className="w-full h-11"
                >
                  {t('running.active.stopDialog.confirm')}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Loading overlay - shown while saving workout after Run Summary */}
      {isSavingRun && createPortal(
        <div 
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center bg-background"
          style={{ 
            paddingTop: SAFE_AREA.top, 
            paddingBottom: SAFE_AREA.bottom, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-base font-medium text-foreground">{t('running.summary.savingTitle')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('running.summary.savingSubtitle')}</p>
        </div>,
        document.body
      )}

      {/* STEP 1: Run Summary - Compact single-page layout, no scroll needed */}
      {showRunSummary && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex flex-col bg-background overflow-hidden ios-modal-scroll"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: `calc(8px + ${SAFE_AREA.top})`, 
            paddingBottom: `calc(16px + ${SAFE_AREA.bottom})`, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
            boxSizing: 'border-box',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
          }}
        >
          {/* Delete - fixed top-right, outside scroll flow, guaranteed to receive touches */}
          <button
            type="button"
            onClick={handleDeleteRun}
            className="fixed min-w-[48px] min-h-[48px] flex items-center justify-center text-destructive hover:text-destructive/80 touch-manipulation z-[100001]"
            style={{ 
              top: `calc(8px + ${SAFE_AREA.top})`, 
              right: `calc(8px + ${SAFE_AREA.right})` 
            }}
          >
            <Trash2 className="h-6 w-6" />
          </button>
          <div className="text-center shrink-0 pb-1">
            <div className="flex items-center justify-center">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                <Footprints className="h-5 w-5 text-primary-foreground" />
              </div>
            </div>
            <h2 className="text-base font-bold text-center text-foreground">
              {t('running.summary.title')}
            </h2>
          </div>
          
          <div className="flex-1 min-h-0 flex flex-col px-4">
            {/* Map thumbnail - tap overlay captures all touches, expand icon bottom-right */}
            {routeLine.length > 1 && workoutSummary?.heartRate ? (
              <SummaryMapHrCarousel
                mapCenter={mapCenterForSummary}
                routeSegments={routeSegmentsForMap}
                routePoints={routePoints}
                routeLine={routeLine}
                coloredRouteSegments={coloredRouteSegmentsForSummary}
                onExpandMap={() => setShowExpandedMap(true)}
                onExpandHr={() => setShowExpandedHr(true)}
                onExpandCombined={() => setShowExpandedCombined(true)}
                expandMapAria={t('running.summary.expandMapAria')}
                expandHrAria={t('running.summary.expandHrAria', { defaultValue: 'Expand heart rate zones' })}
                expandCombinedAria={t('running.summary.expandCombinedAria', { defaultValue: 'Expand route and heart rate' })}
                heartRate={workoutSummary.heartRate}
              />
            ) : routeLine.length > 1 ? (
              <div
                className="shrink-0 mb-2 rounded-lg overflow-hidden border border-border/30 h-44 relative cursor-pointer"
                style={{ minHeight: 176 }}
              >
                <PlatformMap
                  key="summary-map"
                  center={mapCenterForSummary}
                  routePoints={routeLine}
                  routeSegments={routeSegmentsForMap}
                  showUserMarker={false}
                  showRoute={true}
                  showStartFinishMarkers={true}
                  showKmMarkers={true}
                  fitToRoute={true}
                  fitPaddingBottom={1.25}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode={true}
                  interactive={false}
                  zoom={12}
                />
                {/* Full-map tap overlay - captures touches so expand works on iOS */}
                <div
                  className="absolute inset-0 z-10 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setShowExpandedMap(true)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setShowExpandedMap(true);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('running.summary.expandMapAria')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowExpandedMap(true);
                    }
                  }}
                />
                {/* Expand icon - bottom right, visual only */}
                <div
                  className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none"
                  aria-hidden
                >
                  <Maximize2 className="h-5 w-5 text-white" />
                </div>
              </div>
            ) : null}
            <div
              className="flex-1 min-h-0 overflow-y-auto space-y-2 py-1 ios-modal-scroll"
              style={{ 
                touchAction: 'pan-y', 
                WebkitOverflowScrolling: 'touch',
                paddingBottom: keyboardHeight > 0 ? keyboardHeight + 48 : undefined,
              } as React.CSSProperties}
            >
            {/* Editable Run Title */}
            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block flex items-center gap-1">
                {t('running.summary.workoutNameLabel')} <Pencil className="h-3 w-3" />
              </label>
              <input
                type="text"
                value={runTitle}
                onChange={(e) => setRunTitle(e.target.value)}
                onBlur={() => {
                  Analytics.runningSummaryTitleEdited();
                }}
                className="w-full text-center font-semibold px-2 py-1.5 rounded-md border border-border bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                placeholder={t('running.summary.runTitlePlaceholder')}
              />
            </div>
            
            {/* Stats Grid */}
            {workoutSummary && (
              <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 rounded-lg p-3 border border-border/30">
                <div className="text-center">
                  <div className="font-bold text-base text-primary">{workoutSummary.distance} km</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statDistance')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{formatTime(elapsedSeconds)}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statTotalTime')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{workoutSummary.pace}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statAvgPacePerKm')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{workoutSummary.estimatedCalories}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statCalories')}</div>
                </div>
              </div>
            )}
            
            {/* Heart rate zones (when no map carousel — otherwise swipe from map) */}
            {workoutSummary?.heartRate && routeLine.length <= 1 ? (
              <HeartRateZoneChart
                zones={workoutSummary.heartRate.zones}
                buckets={workoutSummary.heartRate.buckets}
                maxHr={workoutSummary.heartRate.maxHr}
                maxHrSource={workoutSummary.heartRate.maxHrSource}
                avgBpm={workoutSummary.heartRate.avgBpm}
                peakBpm={workoutSummary.heartRate.peakBpm}
                targetZone={workoutSummary.heartRate.targetZone}
              />
            ) : !workoutSummary?.heartRate ? (
              <div className="rounded-md border border-dashed border-border/60 bg-muted/10 p-3 text-center">
                <div className="text-xs font-medium text-muted-foreground">
                  Heart rate zones
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Connect Apple Health and wear your Apple Watch during your next run to see time spent in each zone.
                </div>
              </div>
            ) : null}

            {/* Km Splits */}
            {kmSplits.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t('running.summary.splitsSection')}</label>
                <div className="space-y-0.5 max-h-24 overflow-y-auto bg-muted/20 rounded-md p-1.5">
                  {kmSplits.map((split, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs px-2 py-1 rounded-md hover:bg-muted/20">
                      <span className="text-muted-foreground font-medium">
                        {split.km >= 1 ? `${split.km} km` : `${split.km} km`}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{split.time}</span>
                        <span className="font-mono font-semibold text-primary">{split.pace}/km</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* How did this run feel? */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('running.summary.feelingPrompt')}</label>
              <div className="flex justify-between gap-1.5 touch-manipulation">
                {feelingOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setFeelingEmoji(option.id);
                        Analytics.runningSummaryFeelingSelected(option.id, option.label);
                      }}
                      className={`flex-1 py-2 rounded-md transition-all flex items-center justify-center ${
                        feelingEmoji === option.id 
                          ? 'bg-primary/20 ring-2 ring-primary scale-110' 
                          : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                      title={option.label}
                    >
                      <IconComponent className={`h-5 w-5 ${feelingEmoji === option.id ? option.color : 'text-muted-foreground'}`} />
                    </button>
                  );
                })}
              </div>
              {feelingEmoji && (
                <p className="text-[10px] text-center text-muted-foreground mt-0.5">
                  {feelingOptions.find(f => f.id === feelingEmoji)?.label}
                </p>
              )}
            </div>
            
            {/* Notes - scroll into view when focused, extra padding when keyboard open */}
            <div className="pb-safe">
              <label className="text-xs text-muted-foreground mb-0.5 block">{t('running.summary.notesLabel')}</label>
              <textarea
                value={runNotes}
                onChange={(e) => setRunNotes(e.target.value)}
                onFocus={(e) => {
                  const target = e.target;
                  setTimeout(() => {
                    if (document.activeElement === target) {
                      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, Capacitor.getPlatform() === 'ios' ? 500 : 400);
                }}
                onBlur={() => {
                  if (runNotes.trim().length > 0) {
                    Analytics.runningSummaryNotesAdded(runNotes.trim().length);
                  }
                }}
                placeholder={t('running.summary.notesPlaceholder')}
                style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 100 }}
                className="w-full min-h-[80px] px-2 py-1.5 text-xs rounded-md border border-border bg-background/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            </div>
            {/* Share & Save Buttons - fixed at bottom, always visible */}
            <div className="flex gap-2 shrink-0 pt-2 touch-manipulation">
              <Button 
                onClick={() => {
                  const distanceKm = workoutSummary?.distance ?? 0;
                  const paceSeconds = distanceKm > 0 ? elapsedSeconds / distanceKm : 0;
                  setWorkoutToShare({
                    id: `run-${Date.now()}`,
                    title:
                      runTitle ||
                      defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
                    duration_minutes: elapsedSeconds / 60,
                    completed_at: new Date().toISOString(),
                    workout_type: gpsWorkoutType.toLowerCase(),
                    distance_km: distanceKm,
                    average_pace: workoutSummary?.pace ?? '--:--',
                    calories_burned: workoutSummary?.estimatedCalories,
                    route_coordinates: routePoints.map(p => ({ lat: p.lat, lng: p.lng })),
                    duration_seconds: elapsedSeconds,
                    pace_seconds_per_km: paceSeconds,
                    splits: kmSplits,
                    heart_rate: workoutSummary?.heartRate
                      ? {
                          zones: workoutSummary.heartRate.zones,
                          buckets: workoutSummary.heartRate.buckets,
                          maxHr: workoutSummary.heartRate.maxHr,
                          avgBpm: workoutSummary.heartRate.avgBpm,
                          peakBpm: workoutSummary.heartRate.peakBpm,
                          targetZone: workoutSummary.heartRate.targetZone,
                        }
                      : undefined,
                  } as ShareableWorkout);
                  setShowShareModal(true);
                }}
                variant="outline"
                className="flex-1 h-10 touch-manipulation"
              >
                <ShareIcon className="h-4 w-4 mr-1.5 text-primary" />
                {t('running.summary.shareButton')}
              </Button>
            <Button 
              onClick={handleSaveRunSummary}
              disabled={isSavingRun}
              className="flex-1 h-10 touch-manipulation bg-gradient-to-r from-primary to-accent text-black font-semibold disabled:opacity-60"
            >
                {isSavingRun ? t('running.summary.savingButton', { defaultValue: 'Saving…' }) : t('running.summary.saveButton')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Share Modal for Run Summary */}
      <ShareWorkoutModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setWorkoutToShare(null);
        }}
        workout={workoutToShare}
      />
      
      {/* STEP 2: Analyze with Arnold Dialog - dark overlay, no X, only two options */}
      <Dialog open={showAnalyzePrompt} onOpenChange={setShowAnalyzePrompt}>
        <DialogContent 
          hideClose 
          overlayClassName="bg-black"
          className="sm:max-w-sm max-w-[calc(100vw-2rem)] bg-background border-2 border-primary/30 shadow-2xl p-4 sm:p-6"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center space-y-3 pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
              <Trophy className="h-8 w-8 text-gold" />
            </div>
            <DialogTitle className="text-xl font-bold text-center text-foreground">
              {t('running.summary.analysisPrompt.title')}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {runTitle}
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <p className="text-center text-sm font-medium text-foreground">
              {t('running.summary.analysisPrompt.body')}
            </p>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleAnalyzeWithArnold}
                className="w-full gap-2 h-11 font-semibold bg-gradient-primary text-primary-foreground hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" />
                {t('running.summary.analysisPrompt.analyzeWithCoach', { coach: profile?.ai_coach_name || 'Arnold' })}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleSkipAnalysis}
                className="w-full h-11 font-semibold border-2"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Battery optimization prompt - Android only, for reliable background tracking */}
      {showBatteryPrompt && createPortal(
        <div 
          className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/70 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">{t('running.active.batteryPrompt.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('running.active.batteryPrompt.body')}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={async () => {
                  setShowBatteryPrompt(false);
                  try {
                    const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
                    await BatteryOptimization.openBatteryOptimizationSettings();
                  } catch (_) {}
                }}
                className="w-full"
              >
                {t('common.openSettings')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowBatteryPrompt(false);
                  localStorage.setItem('straiv_battery_prompt_dismissed', 'true');
                }}
                className="w-full"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog - must appear above Run Summary (z-99999) */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent 
          className="bg-background border-border z-[100000]"
          overlayClassName="z-[100000]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('running.summary.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('running.summary.deleteDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="touch-manipulation min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteRun}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 touch-manipulation min-h-[44px]"
            >
              {t('running.summary.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {showExpandedCombined && createPortal(
        <div
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">{runTitle || t('running.summary.expandedMapFallbackTitle')}</div>
              <div className="text-xs text-muted-foreground">
                {workoutSummary?.distance || 0} km • {formatTime(elapsedSeconds)} • Route + HR
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedCombined(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col gap-3 px-2 pb-2">
            {routeLine.length > 1 && (
              <div className="flex-1 min-h-[40%] rounded-lg overflow-hidden border border-border/30">
                <PlatformMap
                  key="expanded-combined-map"
                  center={routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null}
                  routeSegments={coloredRouteSegmentsForSummary.length > 0 ? undefined : routeSegmentsForMap}
                  coloredRouteSegments={coloredRouteSegmentsForSummary.length > 0 ? coloredRouteSegmentsForSummary : undefined}
                  showUserMarker={false}
                  showRoute
                  showStartFinishMarkers
                  showKmMarkers
                  fitToRoute
                  className="w-full h-full"
                  darkMode
                  interactive
                  zoom={14}
                />
              </div>
            )}
            {workoutSummary?.heartRate ? (
              <div className="shrink-0 space-y-3 rounded-lg border border-border/30 bg-muted/10 p-3 overflow-y-auto max-h-[45%]">
                <RouteHrEffortChart
                  routePoints={routePoints}
                  hrSamples={workoutSummary.heartRate.samples ?? []}
                  zones={workoutSummary.heartRate.zones}
                />
                <HeartRateZoneChart
                  zones={workoutSummary.heartRate.zones}
                  buckets={workoutSummary.heartRate.buckets}
                  maxHr={workoutSummary.heartRate.maxHr}
                  maxHrSource={workoutSummary.heartRate.maxHrSource}
                  avgBpm={workoutSummary.heartRate.avgBpm}
                  peakBpm={workoutSummary.heartRate.peakBpm}
                  targetZone={workoutSummary.heartRate.targetZone}
                />
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}

      {showExpandedHr && workoutSummary?.heartRate && createPortal(
        <div
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">Heart rate zones</div>
              <div className="text-xs text-muted-foreground">
                Avg {workoutSummary.heartRate.avgBpm ?? '—'} bpm • Peak {workoutSummary.heartRate.peakBpm ?? '—'} bpm
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedHr(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <HeartRateZoneChart
              zones={workoutSummary.heartRate.zones}
              buckets={workoutSummary.heartRate.buckets}
              maxHr={workoutSummary.heartRate.maxHr}
              maxHrSource={workoutSummary.heartRate.maxHrSource}
              avgBpm={workoutSummary.heartRate.avgBpm}
              peakBpm={workoutSummary.heartRate.peakBpm}
              targetZone={workoutSummary.heartRate.targetZone}
              className="text-sm"
            />
          </div>
        </div>,
        document.body
      )}

      {/* Expanded Map - full-screen overlay, MUST be above Run Summary (z-99999) */}
      {showExpandedMap && createPortal(
        <div 
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          {/* Header with close */}
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">{runTitle || t('running.summary.expandedMapFallbackTitle')}</div>
              <div className="text-xs text-muted-foreground">
                {workoutSummary?.distance || 0} km • {formatTime(elapsedSeconds)}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedMap(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          {/* Map - full height, scrollable/zoomable */}
          <div className="flex-1 min-h-0 relative">
            {routeLine.length > 1 && (
              <PlatformMap
                key="expanded-map"
                center={routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null}
                routePoints={routeLine}
                routeSegments={coloredRouteSegmentsForSummary.length > 0 ? undefined : routeSegmentsForMap}
                coloredRouteSegments={coloredRouteSegmentsForSummary.length > 0 ? coloredRouteSegmentsForSummary : undefined}
                showUserMarker={false}
                showRoute={true}
                showStartFinishMarkers={true}
                showKmMarkers={true}
                fitToRoute={true}
                className="w-full h-full"
                darkMode={true}
                interactive={true}
                zoom={14}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

